import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { lockBodyScroll } from '@/utils/body-scroll-lock';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { debounce } from '@/utils/debounce';
import { sessionShuffle } from '@/utils/session-shuffle';
import { useAppTheme } from '@/utils/theme-context';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import {
  Heart, BookOpen, Menu,
  Wallet, Lock, ImageIcon, TrendingUp, Trash2, ShieldCheck, Plus,
  Pencil, Trash, Eraser, Film, Bell, Sparkles, Receipt,
  X, ChevronLeft,
  Copy, ChevronRight, Palette, Settings,
  Mountain, Moon, Zap, Globe, Layers, FolderCog, ArrowLeft,
  Filter, Check, RotateCcw, User, Users, Upload, Play, Pause, Archive,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SectionArrowSwitcher, SECTIONS } from '@/components/SectionSwitcher';
import { CategoryChipsRow, CategoryFilterKey, FILTER_LABELS as SEE_ALL_FILTER_LABELS } from '@/components/CategoryChipsRow';
import { CollapsibleSearch } from '@/components/CollapsibleSearch';
import { SkrTokenIcon } from '@/components/SkrTokenIcon';
import { toast } from 'sonner';
import { haptic } from '@/utils/haptic';
import { notify /*, getNotificationsEnabled, setNotificationsEnabled */ } from '@/utils/notifications'; // getNotificationsEnabled/setNotificationsEnabled temporarily unused — restore when notifications preferences are wired up
import { useAuth, getIdToken } from '@pooflabs/web';
import { AuthContextType } from '@/components/types';
import { WalletButton } from '@/components/WalletButton';
import { CategoryPageShell } from '@/components/CategoryPageShell';
import { WallpaperCard } from '@/components/WallpaperCard';
import VirtualWallpaperGrid from '@/components/VirtualWallpaperGrid';
import { WallpaperExploreGrid } from '@/components/WallpaperExploreGrid';
import { AdminEditableCard } from '@/components/AdminEditableCard';
import { WallpaperViewer } from '@/components/WallpaperViewer';
import { TrashView } from '@/components/TrashView';
import { AdminAddWallpaper } from '@/components/AdminAddWallpaper';
import { AdminArchiveWallpapers } from '@/components/AdminArchiveWallpapers';
import { AdminTransactionsModal } from '@/components/AdminTransactions';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyWallpapers, WallpapersResponse, updateWallpapers } from '@/lib/collections/wallpapers';
import { subscribeManyPurchases, PurchasesResponse } from '@/lib/collections/purchases';
import { setPurchaseAndCaptureSignature } from '@/hooks/use-purchase-flow';
import { subscribeManyNotificationsItems, NotificationsItemsResponse } from '@/lib/collections/notifications';
import { runTokenBalanceQueryForCommonQueries } from '@/lib/collections/commonQueries';
import {
  subscribeManyUsersPurchases,
  subscribeManyUsersFavorites,
  subscribeManyUsersTrash,
  getManyUsersPurchases,
  setUsersFavorites,
  setUsersTrash,
  buildUsersTrash,
  deleteUsersFavorites,
  deleteUsersTrash,
  deleteUsersPurchases,
  UsersPurchasesResponse,
  UsersFavoritesResponse,
  UsersTrashResponse,
} from '@/lib/collections/users';
import { Time, Address, setMany } from '@/lib/db-client';
import { useBackButtonClose } from '@/hooks/use-back-button-close';
import { isCleanupBackInFlight, hasActiveOverlays } from '@/utils/back-button-coordinator';
import { SKR_PRICE_RAW, ADMIN_ADDRESS, SKR_TOKEN_MINT } from '@/lib/constants';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { LibraryView } from '@/components/LibraryView';
import { FavoritesView } from '@/components/FavoritesView';
import { RingtoneFullScreenPlayer } from '@/components/RingtoneFullScreenPlayer';
import { useRingtonePlayer } from '@/contexts/RingtonePlayerContext';
import { MiniPlayerBar } from '@/components/MiniPlayerBar';
import { subscribeUserAvatars, subscribeManyUserAvatars, UserAvatarsResponse } from '@/lib/collections/userAvatars';
import { subscribeUserProfiles, subscribeManyUserProfiles, UserProfilesResponse } from '@/lib/collections/userProfiles';
import { CreatorAvatar } from '@/components/CreatorAvatar';
import AvatarCropModal from '@/components/AvatarCropModal';
import { MyUploadsPanel } from '@/components/MyUploadsPanel';
import { AdminModerationPanel } from '@/components/AdminModerationPanel';
import { ProfileSetupGate } from '@/components/ProfileSetupGate';
import { RINGTONE_CATEGORIES } from '@/components/UserUploadModal';
import { useCategoryOrder } from '@/hooks/use-category-order';
import { useTabSwipe } from '@/hooks/use-tab-swipe';
import { subscribeSiteSettings, setSiteSettings, SiteSettingsResponse } from '@/lib/collections/siteSettings';
import { useWallpaperCategoriesFromDB, saveWallpaperCategoriesToDB } from '@/utils/wallpaperCategories';
import {
  getStoredActiveSection,
  setStoredActiveSection,
  getStoredActiveTab,
  setStoredActiveTab,
} from '@/utils/explore-navigation-state';

import { prefetchAllRoutes } from '@/App';
import { TransactionsList } from '@/components/TransactionsList';
import { SubscriptionsDialog } from '@/components/SubscriptionsDialog';
import { SettingsContent } from '@/components/SettingsContent';
import {
  subscribeManySubscriptionsCreators,
  SubscriptionsCreatorsResponse,
} from '@/lib/collections/subscriptions';

import type { Tab } from '@/components/CategoryPageShell';
type PriceFilter = 'all' | 'new';
type SortOrder = 'newest' | 'oldest' | 'popular';
type SortState = SortOrder | null;
const SORT_LABELS: Record<SortOrder, string> = { newest: 'Newest', oldest: 'Oldest', popular: 'Popular' };

// SeeAll modal "All" dropdown now reuses CategoryFilterKey / FILTER_LABELS
// from CategoryChipsRow (imported above as SEE_ALL_FILTER_LABELS) — the two
// were structurally identical unions, collapsed into one.
type SeeAllFilterKey = CategoryFilterKey;

/**
 * Single source of truth for the "All" filter dropdown's sort behavior,
 * shared by the three Explore section parent memos AND the SeeAll modal's
 * internal sort (previously each reimplemented createdAt-desc separately).
 * - 'new': createdAt desc (falls back to tarobase_created_at)
 * - 'popular': downloadCount desc, tiebreak createdAt desc
 * - 'price_low' / 'price_high': price asc/desc, tiebreak createdAt desc
 * - 'all': no reordering
 */
function sortByFilterKey<
  T extends { price?: number | null; downloadCount?: number | null; createdAt?: number | null; tarobase_created_at?: number | null }
>(items: T[], key: CategoryFilterKey): T[] {
  const createdAtOf = (item: T) => item.createdAt ?? item.tarobase_created_at ?? 0;
  switch (key) {
    case 'new':
      return [...items].sort((a, b) => createdAtOf(b) - createdAtOf(a));
    case 'popular':
      return [...items].sort((a, b) => {
        const diff = (b.downloadCount ?? 0) - (a.downloadCount ?? 0);
        return diff !== 0 ? diff : createdAtOf(b) - createdAtOf(a);
      });
    case 'price_low':
      return [...items].sort((a, b) => {
        const diff = (a.price ?? 0) - (b.price ?? 0);
        return diff !== 0 ? diff : createdAtOf(b) - createdAtOf(a);
      });
    case 'price_high':
      return [...items].sort((a, b) => {
        const diff = (b.price ?? 0) - (a.price ?? 0);
        return diff !== 0 ? diff : createdAtOf(b) - createdAtOf(a);
      });
    case 'all':
    default:
      return items;
  }
}

const PRICE_FILTER_OPTIONS: { value: PriceFilter; label: string; price?: number }[] = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
];

function shuffleArray<T>(items: T[]): T[] {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

type ResolvedMediaType = 'wallpaper' | 'live-wallpaper' | 'ringtone' | 'icon';

function resolveMediaType(w: WallpapersResponse): ResolvedMediaType {
  const stored = (w as WallpapersResponse & { mediaType?: string }).mediaType;
  if (stored === 'wallpaper' || stored === 'live-wallpaper' || stored === 'ringtone' || stored === 'icon') {
    return stored;
  }
  // Legacy items without mediaType: detect from URL extension
  const url = (w.imageUrl || '').toLowerCase().split('?')[0];
  if (/\.(mp4|webm|mov|m4v)$/.test(url)) return 'live-wallpaper';
  if (/\.(mp3|wav|ogg|m4a|aac)$/.test(url)) return 'ringtone';
  return 'wallpaper';
}

function useSkrBalance(userAddress: string | undefined, refreshKey: number = 0) {
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    if (!userAddress) { setBalance(null); return; }
    let cancelled = false;
    const fetchBalance = async () => {
      try {
        const raw = await runTokenBalanceQueryForCommonQueries('balance-check', {
          walletAddress: userAddress,
          tokenMint: SKR_TOKEN_MINT,
        });
        if (!cancelled) setBalance(raw / 1_000_000);
      } catch {
        if (!cancelled) setBalance(null);
      }
    };
    fetchBalance();
    return () => { cancelled = true; };
  }, [userAddress, refreshKey]);

  return balance;
}

interface AuthGateProps {
  title: string;
  subtitle: string;
  onConnect: () => void;
}

const AuthGate: React.FC<AuthGateProps> = ({ title, subtitle, onConnect }) => {
  const { tokens } = useAppTheme();
  return (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.38, ease: 'easeOut' }}
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '32px', gap: 20, textAlign: 'center',
      minHeight: 'calc(100vh - 180px)',
    }}
  >
    {/* Outer glow ring */}
    <div style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute', inset: -8,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <motion.div
        animate={{ scale: [1, 1.04, 1] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(109,40,217,0.1) 100%)',
          border: '1px solid rgba(139,92,246,0.35)',
          boxShadow: '0 0 24px rgba(139,92,246,0.2), inset 0 1px 0 rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Lock size={28} color="#a78bfa" strokeWidth={1.8} />
      </motion.div>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{
        color: tokens.textPrimary, fontSize: 17, fontWeight: 700,
        margin: 0, maxWidth: 280, lineHeight: 1.35,
        fontFamily: "'Syne', sans-serif", letterSpacing: '-0.01em',
      }}>
        {title}
      </p>
      <p style={{
        color: tokens.textMuted, fontSize: 13,
        margin: 0, maxWidth: 260, lineHeight: 1.55,
        fontFamily: "'Syne', sans-serif",
      }}>
        {subtitle}
      </p>
    </div>

    <motion.button
      whileHover={{ scale: 1.03, boxShadow: '0 0 22px rgba(139,92,246,0.45)' }}
      whileTap={{ scale: 0.97 }}
      onClick={onConnect}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '11px 26px', borderRadius: 12,
        background: 'rgba(139,92,246,0.85)',
        border: '1px solid rgba(167,139,250,0.4)',
        color: '#fff', fontSize: 14, fontWeight: 700,
        cursor: 'pointer', fontFamily: "'Syne', sans-serif",
        boxShadow: '0 4px 16px rgba(139,92,246,0.3)',
        transition: 'box-shadow 0.2s ease',
      }}
    >
      <Wallet size={15} strokeWidth={2.2} />
      Connect Wallet
    </motion.button>
  </motion.div>
  );
};


interface MenuPanelProps {
  user: { address: string } | null;
  skrBalance: number | null;
  purchaseCount: number;
  favoriteCount: number;
  trashCount: number;
  onOpenTrash: () => void;
  onOpenMyUploads?: () => void;
  onOpenSales?: () => void;
  onOpenSubscriptions?: () => void;
  subscriptionCount?: number;
  onNavigateSettings?: () => void;
  onLogout?: () => void | Promise<void>;
  accentColor?: string;
  existingCategories?: string[];
  /** Username from userProfiles collection (if set) */
  userProfileUsername?: string | null;
  /** Whether the user has a complete creator profile (username + avatar). Avatar click is a no-op when false. */
  userProfileComplete?: boolean;
  /** Count of wallpapers uploaded by this user */
  myUploadCount?: number;
  /** Total SKR earned from sales (human-readable, already formatted) */
  salesSkrTotal?: string;
  /** Current explore mode controlled by admin */
  exploreMode?: 'all-only' | 'tabs-only';
  /** Toggle the explore mode between all-only and tabs-only */
  onToggleExploreMode?: () => void;
}

const GRID_COLUMN_OPTIONS: { value: number; label: string }[] = [
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
];

const MenuPanel: React.FC<MenuPanelProps> = ({ user, skrBalance, purchaseCount, favoriteCount, trashCount, onOpenTrash, onOpenMyUploads, onOpenSales, onOpenSubscriptions, subscriptionCount = 0, onNavigateSettings, onLogout, accentColor = '#a78bfa', existingCategories, userProfileUsername, userProfileComplete = false, myUploadCount = 0, salesSkrTotal = '0', exploreMode = 'tabs-only', onToggleExploreMode }) => {
  const { tokens } = useAppTheme();
  const hexToRgbStr = (hex: string) => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!r) return '167,139,250';
    return `${parseInt(r[1],16)},${parseInt(r[2],16)},${parseInt(r[3],16)}`;
  };
  const rgb = hexToRgbStr(accentColor);

  // ── Scroll-collapse hero transforms ────────────────────────────────────────
  const { scrollY } = useScroll();
  const SCROLL_RANGE: [number, number] = [0, 150];

  const rawAvatarSize = useTransform(scrollY, SCROLL_RANGE, [108, 48]);
  const avatarSize = useSpring(rawAvatarSize, { stiffness: 100, damping: 20 });

  const rawHeroOpacity = useTransform(scrollY, SCROLL_RANGE, [1, 0]);
  const heroOpacity = useSpring(rawHeroOpacity, { stiffness: 100, damping: 20 });

  const rawHeroPaddingTop = useTransform(scrollY, SCROLL_RANGE, [18, 8]);
  const heroPaddingTop = useSpring(rawHeroPaddingTop, { stiffness: 100, damping: 20 });

  const rawHeroPaddingBottom = useTransform(scrollY, SCROLL_RANGE, [20, 8]);
  const heroPaddingBottom = useSpring(rawHeroPaddingBottom, { stiffness: 100, damping: 20 });

  const rawHeroGap = useTransform(scrollY, SCROLL_RANGE, [30, 8]);
  const heroGap = useSpring(rawHeroGap, { stiffness: 100, damping: 20 });

  const rawUsernameSize = useTransform(scrollY, SCROLL_RANGE, [20, 15]);
  const usernameSize = useSpring(rawUsernameSize, { stiffness: 100, damping: 20 });

  const [addressCopied, setAddressCopied] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  // Temporarily hidden — restore when notifications preferences are wired up
  // ── Notifications panel state ─────────────────────────────────────────────
  // const [notificationsOpen, setNotificationsOpen] = useState(false);
  // const [notificationsEnabled, setNotificationsEnabledState] = useState(() =>
  //   getNotificationsEnabled(user?.address)
  // );
  //
  // const handleToggleNotifications = (enabled: boolean) => {
  //   setNotificationsEnabledState(enabled);
  //   setNotificationsEnabled(enabled, user?.address);
  // };
  //
  // ── Delete profile (parked inside Settings) ───────────────────────────────
  // const [settingsDeleteDialogOpen, setSettingsDeleteDialogOpen] = useState(false);
  // const [settingsDeleting, setSettingsDeleting] = useState(false);
  //
  // const handleSettingsDeleteProfile = async () => {
  //   if (!user) return;
  //   setSettingsDeleting(true);
  //   try {
  //     const token = await getIdToken();
  //     if (!token) {
  //       notify.error('Authentication error. Please reconnect your wallet.', user.address);
  //       return;
  //     }
  //     const authApi = createAuthenticatedApiClient(token, user.address);
  //     await authApi.delete('/api/profile');
  //     notify.success('Profile deleted', user.address);
  //     navigate('/');
  //   } catch (err) {
  //     const msg = err instanceof Error ? err.message : 'Failed to delete profile';
  //     notify.error(msg, user.address);
  //   } finally {
  //     setSettingsDeleting(false);
  //     setSettingsDeleteDialogOpen(false);
  //   }
  // };

  // ── Avatar upload state ───────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarHovered, setAvatarHovered] = useState(false);

  // Reset badge when native file-picker cancel fires (not in React's type defs)
  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) return;
    const handleCancel = () => setAvatarHovered(false);
    input.addEventListener('cancel', handleCancel);
    return () => input.removeEventListener('cancel', handleCancel);
  }, []);

  // Subscribe to the user's avatar from the userAvatars collection
  const { data: avatarData } = useRealtimeData<UserAvatarsResponse | null>(
    subscribeUserAvatars,
    !!user?.address,
    user?.address ?? '',
  );

  const avatarUrl = avatarData?.avatarUrl ?? null;

  const handleAvatarClick = () => {
    if (!user) return;
    // Only allow avatar editing when the user already has a creator profile
    if (!userProfileComplete) return;
    setAvatarHovered(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setCropImageSrc(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const shortAddress = user
    ? `${user.address.slice(0, 4)}...${user.address.slice(-4)}`
    : '';

  const handleCopyAddress = () => {
    if (!user) return;
    navigator.clipboard.writeText(user.address).then(() => {
      setAddressCopied(true);
      setTimeout(() => setAddressCopied(false), 2000);
    });
  };

  const skrFormatted = skrBalance !== null
    ? skrBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';

  return (
    <div style={{ paddingBottom: 110 }}>

      {/* ── Scroll-collapse sticky hero ──────────────────────────────── */}
      <motion.div
        style={{
          position: 'sticky',
          top: `calc(63px + env(safe-area-inset-top, 0px))`,
          zIndex: 100,
          background: tokens.bgHeader,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          paddingTop: heroPaddingTop,
          paddingBottom: heroPaddingBottom,
          paddingLeft: 14,
          paddingRight: 14,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: heroGap,
        }}
      >
        {user ? (
          <>
            {/* Avatar row + username in one row when collapsed */}
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 14, justifyContent: 'center' }}>
              {/* Avatar — shrinks 100→40, wrapped in gradient ring */}
              <motion.div
                style={{
                  width: avatarSize,
                  height: avatarSize,
                  borderRadius: '50%',
                  background: `rgba(${rgb},0.18)`,
                  padding: 0,
                  boxShadow: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  cursor: userProfileComplete ? 'pointer' : 'default',
                  position: 'relative',
                }}
                onClick={() => { if (user && userProfileComplete) handleAvatarClick(); }}
                onHoverStart={() => { if (userProfileComplete) setAvatarHovered(true); }}
                onHoverEnd={() => setAvatarHovered(false)}
              >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: avatarUrl
                    ? 'transparent'
                    : `rgba(${rgb},0.18)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => { setAvatarHovered(false); handleFileChange(e); }}
                />
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <User size={41} color={accentColor} strokeWidth={1.6} />
                )}

                {/* Upload spinner overlay */}
                {avatarUploading && (
                  <div style={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: '50%',
                      border: '2px solid rgba(167,139,250,0.3)',
                      borderTopColor: '#a78bfa',
                      animation: 'avatar-spin 0.7s linear infinite',
                    }} />
                  </div>
                )}

                {/* Camera edit hint on hover */}
                {user && userProfileComplete && !avatarUploading && (
                  <div style={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    background: avatarHovered ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0)',
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                    paddingBottom: 4,
                    transition: 'background 0.2s',
                    pointerEvents: 'none',
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: `rgba(${rgb},0.9)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      opacity: avatarHovered ? 1 : 0,
                      transition: 'opacity 0.2s',
                    }} />
                  </div>
                )}
              </div>
              </motion.div>

              {/* Username — font shrinks 20→15 */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                  {(() => {
                    const displayText = userProfileUsername ? `@${userProfileUsername}` : shortAddress;
                    const adaptiveFontSize = displayText.length <= 8 ? 26 : displayText.length <= 11 ? 23 : 19;
                    const isOutfitName = !!userProfileUsername;
                    return (
                      <motion.span
                        style={{
                          fontSize: adaptiveFontSize,
                          fontWeight: isOutfitName ? 700 : 500,
                          color: tokens.textPrimary,
                          fontFamily: isOutfitName ? "'Syne', sans-serif" : "'DM Mono', monospace",
                          letterSpacing: '-0.01em',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          display: 'block',
                          maxWidth: 'calc(100vw - 180px)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {displayText}
                      </motion.span>
                    );
                  })()}
                </div>

                {/* Address + SKR balance — fade out on scroll */}
                <motion.div
                  style={{
                    opacity: heroOpacity,
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{
                      fontSize: 13, color: tokens.textVeryMuted,
                      fontFamily: "'DM Mono', monospace",
                    }}>
                      {shortAddress}
                    </span>
                    <button
                      onClick={handleCopyAddress}
                      title="Copy address"
                      style={{
                        background: 'none', border: 'none', padding: 1, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        color: addressCopied ? accentColor : tokens.textVeryMuted,
                        transition: 'color 0.2s ease',
                        flexShrink: 0,
                      }}
                    >
                      <Copy size={12} strokeWidth={2} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: tokens.textVeryMuted, fontFamily: "'DM Mono', monospace" }}>
                    <SkrTokenIcon size={12} />
                    <span className="skr-shimmer-text">{skrFormatted} SKR</span>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Stats row — fades out on scroll */}
            <motion.div
              style={{
                opacity: heroOpacity,
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <div style={{
                borderRadius: 16, background: '#0E101A',
                overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '12px 0',
              }}>
                {/* Purchases */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: tokens.textPrimary,
                    fontFamily: "'Outfit', sans-serif", lineHeight: 1,
                  }}>
                    {purchaseCount}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 500, color: tokens.textVeryMuted,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                    Purchases
                  </span>
                </div>

                {/* Favorites */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: tokens.textPrimary,
                    fontFamily: "'Outfit', sans-serif", lineHeight: 1,
                  }}>
                    {favoriteCount}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 500, color: tokens.textVeryMuted,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                    Favorites
                  </span>
                </div>

                {/* Subscriptions */}
                <div
                  onClick={() => onOpenSubscriptions?.()}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                >
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: tokens.textPrimary,
                    fontFamily: "'Outfit', sans-serif", lineHeight: 1,
                  }}>
                    {subscriptionCount}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 500, color: tokens.textVeryMuted,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                    Subs
                  </span>
                </div>
              </div>
            </motion.div>

          </>
        ) : (
          <div style={{
            borderRadius: 14, padding: '14px 16px',
            background: tokens.bgCard,
            border: `1px solid ${tokens.border}`,
            display: 'flex', justifyContent: 'center',
            width: '100%',
          }}>
            <WalletButton variant="dark" />
          </div>
        )}
      </motion.div>

      <style>{`
        @keyframes avatar-spin { to { transform: rotate(360deg); } }
        .skr-shimmer-text {
          background: linear-gradient(90deg, #a855f7 0%, #46f5e0 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          display: inline-block;
        }
      `}</style>

      {/* ── Menu content below the sticky hero ──────────────────────── */}
      <div style={{ padding: '8px 14px 0' }}>

      {/* ── Menu groups ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 10 }}>

        {/* CREATOR section — logged in only */}
        {user && (
          <div>
            <div style={{ borderRadius: 16, background: '#0E101A', overflow: 'hidden' }}>

              {/* My Uploads */}
              <motion.div
                whileTap={{ scale: 0.99 }}
                onClick={() => onOpenMyUploads?.()}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 16px', cursor: 'pointer' }}
              >
                <Upload size={19} color="#46f5e0" strokeWidth={1.8} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: tokens.textPrimary, fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>My Uploads</div>
                  <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2, fontFamily: "'Outfit', sans-serif" }}>
                    {myUploadCount > 0 ? `${myUploadCount} item${myUploadCount !== 1 ? 's' : ''}` : 'No uploads yet'}
                  </div>
                </div>
                <ChevronRight size={16} color={tokens.textVeryMuted} style={{ flexShrink: 0 }} />
              </motion.div>

              {userProfileComplete && (
                <>
                  <motion.div
                    whileTap={{ scale: 0.99 }}
                    onClick={() => onOpenSales?.()}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 16px', cursor: 'pointer' }}
                  >
                    <TrendingUp size={19} color="#f59e0b" strokeWidth={1.8} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: tokens.textPrimary, fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>Sales</div>
                      <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2, fontFamily: "'Outfit', sans-serif" }}>
                        {salesSkrTotal !== '0' ? `${salesSkrTotal} SKR earned` : 'No sales yet'}
                      </div>
                    </div>
                    <ChevronRight size={16} color={tokens.textVeryMuted} style={{ flexShrink: 0 }} />
                  </motion.div>
                </>
              )}

            </div>
          </div>
        )}

        {/* APP section */}
        <div>
          <div style={{ borderRadius: 16, background: '#0E101A', overflow: 'hidden' }}>

            {/* Settings */}
            <motion.div
              whileTap={{ scale: 0.99 }}
              onClick={() => setSettingsExpanded(prev => !prev)}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 16px', cursor: 'pointer' }}
            >
              <Settings size={19} color={accentColor} strokeWidth={1.8} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: tokens.textPrimary, fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>Settings</div>
              </div>
              <motion.div
                animate={{ rotate: settingsExpanded ? 90 : 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                style={{ flexShrink: 0, display: 'flex' }}
              >
                <ChevronRight size={16} color={tokens.textVeryMuted} />
              </motion.div>
            </motion.div>

            {/* Inline settings accordion */}
            <motion.div
              animate={{ gridTemplateRows: settingsExpanded ? '1fr' : '0fr' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{ display: 'grid', overflow: 'hidden' }}
            >
              <div style={{ overflow: 'hidden' }}>
                <div style={{ borderTop: `1px solid ${tokens.borderSubtle}` }}>
                  <SettingsContent />
                </div>
              </div>
            </motion.div>

            {user && (
              <>
                <motion.div
                  whileTap={{ scale: 0.99 }}
                  onClick={onOpenTrash}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 16px', cursor: 'pointer' }}
                >
                  <Trash2 size={19} color="#ef4444" strokeWidth={1.8} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: tokens.textPrimary, fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>Trash</div>
                    <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2, fontFamily: "'Outfit', sans-serif" }}>{trashCount} item{trashCount !== 1 ? 's' : ''}</div>
                  </div>
                  <ChevronRight size={16} color={tokens.textVeryMuted} style={{ flexShrink: 0 }} />
                </motion.div>
              </>
            )}

          </div>
        </div>

      </div>

      {/* Log Out — plain text link */}
      {user && onLogout && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
          <button
            onClick={async () => { try { await onLogout(); } catch {} }}
            style={{
              background: 'none', border: 'none', padding: '4px 8px',
              cursor: 'pointer',
              color: 'rgba(239,68,68,0.5)',
              fontSize: 13, fontWeight: 400,
              fontFamily: "'Outfit', sans-serif",
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(239,68,68,0.8)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(239,68,68,0.5)'; }}
          >
            Log Out
          </button>
        </div>
      )}

      {/* Avatar Crop Modal */}
      {cropImageSrc && user && (
        <AvatarCropModal
          imageSrc={cropImageSrc}
          walletAddress={user.address}
          onClose={() => setCropImageSrc(null)}
          onSuccess={(url) => {
            // The realtime subscription will update avatarUrl automatically
            setCropImageSrc(null);
          }}
        />
      )}

      </div>
    </div>
  );
};

// ── Suggestion icon map for "no results" view ──
const SUGGESTION_ICONS: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  Nature: Mountain,
  Abstract: Sparkles,
  Minimal: Layers,
  Dark: Moon,
  Gradient: Palette,
  Space: Globe,
  Urban: TrendingUp,
  Cyberpunk: Zap,
};

const SUGGESTION_TAGLINES: Record<string, string> = {
  Nature: 'Earthy, alive, free',
  Abstract: 'Bold, unexpected forms',
  Minimal: 'Less, but felt deeper',
  Dark: 'Quiet, deep, immersive',
  Gradient: 'Soft color journeys',
  Space: 'Vast, calm, infinite',
  Urban: 'Energy of the city',
  Cyberpunk: 'Neon-lit futures',
};

// Deterministic-ish hash so the SAME query gets the same pair, but different queries differ
const hashString = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

interface NoSearchResultsProps {
  query: string;
  categories: string[];
  activeCategory: string;
  onClear: () => void;
  onTryAnother: () => void;
  onPickCategory: (cat: string) => void;
  accent?: string;
}

const NoSearchResults: React.FC<NoSearchResultsProps> = ({
  query, categories, activeCategory, onClear, onTryAnother, onPickCategory,
  accent = '#a78bfa',
}) => {
  const { tokens } = useAppTheme();
  const cyan = '#46f5e0';
  const suggestions = useMemo(() => {
    const pool = categories.filter(c => c !== 'All' && c !== activeCategory && SUGGESTION_ICONS[c]);
    if (pool.length === 0) return [] as { cat: string; tagline: string }[];
    const seed = hashString(query.toLowerCase());
    const chosen: string[] = [];
    const cats = [...pool];
    let s = seed;
    while (chosen.length < Math.min(3, cats.length)) {
      const idx = s % cats.length;
      chosen.push(cats[idx]);
      cats.splice(idx, 1);
      s = Math.floor(s / Math.max(1, cats.length || 1)) + 17;
    }
    return chosen.map(cat => ({ cat, tagline: SUGGESTION_TAGLINES[cat] ?? cat }));
  }, [query, categories, activeCategory]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '16px 16px 28px', textAlign: 'center',
    }}>
      {/* ── Circular illustration (compact) ── */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        style={{
          position: 'relative',
          width: 132, height: 132,
          marginTop: 4, marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: '50%',
          background: `radial-gradient(circle at center, rgba(167,139,250,0.18) 0%, rgba(167,139,250,0.04) 50%, rgba(0,0,0,0) 72%)`,
        }} />
        <div style={{
          position: 'absolute', inset: 4,
          borderRadius: '50%',
          border: '1px solid rgba(167,139,250,0.22)',
        }} />
        <motion.div
          initial={{ rotate: -6 }}
          animate={{ rotate: [-6, 6, -6] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div style={{
            width: 62, height: 62,
            borderRadius: '50%',
            border: `2px solid ${accent}`,
            background: 'rgba(167,139,250,0.10)',
            boxShadow: '0 6px 20px rgba(167,139,250,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative',
          }}>
            <X size={24} color={'#fff'} strokeWidth={2.4} />
            <div style={{
              position: 'absolute',
              right: -18, bottom: -8,
              width: 30, height: 7,
              borderRadius: 4,
              background: accent,
              transform: 'rotate(40deg)',
              transformOrigin: 'left center',
              boxShadow: '0 3px 10px rgba(167,139,250,0.45)',
            }} />
          </div>
        </motion.div>
      </motion.div>

      {/* ── Headline + body ── */}
      <h3 style={{
        margin: 0,
        fontFamily: "'Syne', sans-serif",
        fontSize: 19, fontWeight: 800, letterSpacing: '-0.01em',
        color: tokens.textPrimary,
      }}>
        No results found
      </h3>
      <p style={{
        margin: '6px 0 16px',
        fontSize: 13, lineHeight: 1.5,
        color: tokens.textMuted,
        maxWidth: 280,
      }}>
        Nothing for "{query}".
      </p>

      {/* ── Primary CTA (centered) ── */}
      <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={onTryAnother}
          style={{
            padding: '10px 28px',
            borderRadius: 9999,
            background: 'rgba(70,245,224,0.06)',
            border: `1px solid ${cyan}`,
            color: cyan,
            fontFamily: "'Syne', sans-serif",
            fontSize: 13, fontWeight: 700, letterSpacing: '0.01em',
            cursor: 'pointer',
          }}
        >
          New search
        </motion.button>
      </div>

      {/* ── Suggestions: real categories, compact chip row ── */}
      {suggestions.length > 0 && (
        <div style={{ width: '100%', marginTop: 22 }}>
          <p style={{
            margin: '0 0 10px',
            fontFamily: "'Syne', sans-serif",
            fontSize: 12, fontWeight: 700,
            color: tokens.textMuted,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}>
            Try a category
          </p>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'center',
          }}>
            {suggestions.map(({ cat, tagline }) => {
              const Icon = SUGGESTION_ICONS[cat] ?? Sparkles;
              return (
                <motion.button
                  key={cat}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onPickCategory(cat)}
                  title={tagline}
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    gap: 7,
                    padding: '8px 14px',
                    borderRadius: 9999,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(167,139,250,0.22)',
                    cursor: 'pointer',
                  }}
                >
                  <Icon size={14} color={cyan} />
                  <span style={{
                    fontFamily: "'Syne', sans-serif",
                    fontSize: 13, fontWeight: 700,
                    color: tokens.textPrimary,
                    letterSpacing: '0.01em',
                  }}>
                    {cat}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

interface GalleryGridProps {
  wallpapers: WallpapersResponse[];
  emptyMessage: string;
  emptyAction?: { label: string; onClick: () => void };
  fromLibrary?: boolean;
  favoritedIds: Set<string>;
  purchasedIds: Set<string>;
  onToggleFavorite: (w: WallpapersResponse) => void;
  onSelectWallpaper: (w: WallpapersResponse, fromLibrary: boolean) => void;
  gridColumns?: number;
  accentColor?: string;
  minimal?: boolean;
  disableVirtualization?: boolean;
}

const GalleryGrid: React.FC<GalleryGridProps> = React.memo(({
  wallpapers, emptyMessage, emptyAction, fromLibrary = false,
  favoritedIds, purchasedIds, onToggleFavorite, onSelectWallpaper,
  gridColumns = 2, accentColor = '#8b5cf6', minimal = false,
  disableVirtualization = false,
}) => {
  return (
    <VirtualWallpaperGrid
      wallpapers={wallpapers}
      favoritedIds={favoritedIds}
      purchasedIds={purchasedIds}
      onToggleFavorite={onToggleFavorite}
      onSelectWallpaper={onSelectWallpaper}
      gridColumns={gridColumns}
      fromLibrary={fromLibrary}
      accentColor={accentColor}
      minimal={minimal}
      emptyMessage={emptyMessage}
      emptyAction={emptyAction}
      disableVirtualization={disableVirtualization}
    />
  );
});
GalleryGrid.displayName = 'GalleryGrid';

// ---------- Ringtone avatar color palette ----------
// Vibrant accent colors for the round waveform icon. Each ringtone gets a
// color derived deterministically from its id so it never flickers on re-render.
const RINGTONE_AVATAR_COLORS = [
  'rgba(0,229,255,1)',    // cyan
  'rgba(168,85,247,1)',   // purple
  'rgba(236,72,153,1)',   // pink
  'rgba(251,146,60,1)',   // orange
  'rgba(52,211,153,1)',   // green
  'rgba(59,130,246,1)',   // blue
  'rgba(250,204,21,1)',   // yellow
  'rgba(239,68,68,1)',    // red
  'rgba(20,184,166,1)',   // teal
  'rgba(196,181,253,1)',  // lavender
  // extended palette — 12 additional distinct hues
  'rgba(255,87,34,1)',    // deep orange
  'rgba(0,230,118,1)',    // neon mint
  'rgba(124,77,255,1)',   // indigo violet
  'rgba(255,214,0,1)',    // vivid gold
  'rgba(0,176,255,1)',    // sky blue
  'rgba(240,98,146,1)',   // rose
  'rgba(100,221,23,1)',   // lime green
  'rgba(255,64,129,1)',   // hot magenta
  'rgba(0,184,212,1)',    // deep cyan
  'rgba(255,171,64,1)',   // amber
  'rgba(149,117,205,1)',  // soft violet
  'rgba(77,208,225,1)',   // ice blue
];

/** Derive a stable color index from a string id using a simple hash. */
function ringtoneColorFromId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return RINGTONE_AVATAR_COLORS[hash % RINGTONE_AVATAR_COLORS.length];
}

// ---------- RingtoneRow (for HomePage Section 2) ----------
// Uses the global RingtonePlayerContext for playback so the mini-player
// and full-screen player stay in sync.

interface RingtoneRowProps {
  wallpaper: WallpapersResponse;
  isFavorited: boolean;
  isPurchased: boolean;
  /** All ringtones in the current list — passed as context playlist */
  playlist: WallpapersResponse[];
  onToggleFavorite: (w: WallpapersResponse) => void;
}

const RingtoneRow: React.FC<RingtoneRowProps> = ({
  wallpaper, isFavorited, isPurchased, playlist, onToggleFavorite,
}) => {
  const { currentRingtone, isPlaying, togglePlayPause, play, openFullScreen, setFavorited, setPurchased, currentTime, duration: globalDuration } = useRingtonePlayer();
  const isThisTrack = currentRingtone?.id === wallpaper.id;
  // Show active state whenever this track is the current track and audio is playing,
  // regardless of whether the floating mini-player is also visible.
  const isThisPlaying = isPlaying && isThisTrack;
  const progress = isThisTrack && globalDuration > 0
    ? (currentTime / globalDuration) * 100
    : 0;

  // Metadata probe (lightweight) — only to show duration in the row
  const [duration, setDuration] = useState(0);
  const probeRef = useRef<HTMLAudioElement>(null);

  const handleProbeLoaded = () => {
    const a = probeRef.current;
    if (a && isFinite(a.duration)) setDuration(a.duration);
  };

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Keep context isFavorited in sync so the full-screen player shows correct state
    setFavorited(isFavorited);
    setPurchased(isPurchased);
    togglePlayPause(wallpaper, playlist);
  };

  const handleCardTap = () => {
    setFavorited(isFavorited);
    setPurchased(isPurchased);
    play(wallpaper, playlist);
    openFullScreen();
  };

  const durationSecs = duration > 0 ? Math.round(duration) : null;
  const category = wallpaper.category ?? 'Audio';
  const subtitle = durationSecs != null ? `${category} • ${durationSecs}s` : category;
  const equalizerColor = ringtoneColorFromId(wallpaper.id);

  return (
    <motion.div
      style={{ width: '100%' }}
      animate={isThisPlaying ? { scale: 1.02, y: -1 } : { scale: 1, y: 0 }}
      whileHover={{ scale: 1.025, y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 320, damping: 22 }}
    >
      {/* Silent metadata probe */}
      <audio
        ref={probeRef}
        src={wallpaper.imageUrl ?? ''}
        preload="metadata"
        onLoadedMetadata={handleProbeLoaded}
        style={{ display: 'none' }}
      />

      <MiniPlayerBar
        title={wallpaper.title ?? 'Untitled'}
        subtitle={subtitle}
        isPlaying={isThisPlaying}
        onPlayPause={handlePlayPause}
        showNextButton={false}
        showDismissButton={false}
        onBarClick={handleCardTap}
        showFavoriteButton
        isFavorited={isFavorited}
        onToggleFavorite={(e) => { e.stopPropagation(); onToggleFavorite(wallpaper); }}
        progress={progress}
        equalizerColor={equalizerColor}
      />
    </motion.div>
  );
};

type CategorySection = 'wallpaper' | 'live-wallpaper' | 'ringtone';

interface ManageCategoriesSheetProps {
  onClose: () => void;
  categories: string[];
  liveWallpaperCategories: string[];
  ringtoneCategories: string[];
  allWallpapers: WallpapersResponse[];
  resolveMediaType: (w: WallpapersResponse) => ResolvedMediaType;
  onRename: (cat: string, section: CategorySection) => void;
  onDelete: (cat: string, section: CategorySection) => void;
  onNewCategory: (mediaType: 'wallpaper' | 'live-wallpaper' | 'ringtone', category: string) => void;
}

const ManageCategoriesSheet: React.FC<ManageCategoriesSheetProps> = ({
  onClose,
  categories,
  liveWallpaperCategories,
  ringtoneCategories,
  allWallpapers,
  resolveMediaType,
  onRename,
  onDelete,
  onNewCategory,
}) => {
  const [inlineAdd, setInlineAdd] = React.useState<CategorySection | null>(null);
  const [inlineVal, setInlineVal] = React.useState('');
  const inlineRef = React.useRef<HTMLInputElement>(null);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    const unlock = lockBodyScroll();
    return unlock;
  }, []);

  React.useEffect(() => {
    if (inlineAdd) { inlineRef.current?.focus(); }
  }, [inlineAdd]);

  const commitInline = () => {
    const t = inlineVal.trim();
    if (t && inlineAdd) onNewCategory(inlineAdd, t);
    setInlineVal('');
    setInlineAdd(null);
  };

  const sections: Array<{ label: string; sectionKey: CategorySection; cats: string[] }> = [
    { label: 'Wallpapers', sectionKey: 'wallpaper', cats: categories.filter(c => c !== 'All') },
    { label: 'Live Wallpapers', sectionKey: 'live-wallpaper', cats: liveWallpaperCategories.filter(c => c !== 'All') },
    { label: 'Ringtones', sectionKey: 'ringtone', cats: ringtoneCategories.filter(c => c !== 'All') },
  ];

  return (
    <AnimatePresence>
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 4000,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(5px)',
          }}
        />
        <div style={{
          position: 'fixed', inset: 0, zIndex: 4001,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16, pointerEvents: 'none',
        }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 12 }}
          transition={{ type: 'spring', stiffness: 400, damping: 32 }}
          style={{
            background: '#111118',
            borderRadius: 18,
            border: '1px solid rgba(255,255,255,0.10)',
            width: 'min(400px, calc(100vw - 32px))',
            maxHeight: 'min(600px, calc(100vh - 64px))',
            overflowY: 'auto',
            boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
            pointerEvents: 'auto',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px 10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <button
                onClick={onClose}
                title="Go back"
                style={{
                  background: 'rgba(139,92,246,0.10)',
                  border: '1px solid rgba(139,92,246,0.22)',
                  borderRadius: 7,
                  cursor: 'pointer',
                  color: '#a78bfa',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 4,
                  transition: 'all 0.15s',
                  width: 26, height: 26,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.22)'; e.currentTarget.style.borderColor = 'rgba(139,92,246,0.45)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.10)'; e.currentTarget.style.borderColor = 'rgba(139,92,246,0.22)'; }}
              >
                <ArrowLeft size={14} />
              </button>
              <span style={{
                fontSize: 13, fontWeight: 700, color: '#e8e8f0',
                fontFamily: "'Syne', sans-serif", letterSpacing: '-0.01em',
              }}>
                Manage Categories
              </span>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', padding: '4px',
                cursor: 'pointer', color: 'rgba(240,240,245,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 6,
              }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Sections */}
          <div style={{ padding: '8px 0 4px' }}>
            {sections.map(({ label, sectionKey, cats }, sIdx) => {
              const sectionItems = allWallpapers.filter(w => resolveMediaType(w) === sectionKey);
              const isAddingHere = inlineAdd === sectionKey;

              return (
                <div key={sectionKey} style={{ marginBottom: sIdx < sections.length - 1 ? 0 : 0 }}>
                  {/* Section header row */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 16px 4px',
                  }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: 'rgba(167,139,250,0.5)',
                      textTransform: 'uppercase', letterSpacing: '0.1em',
                      fontFamily: "'Syne', sans-serif",
                    }}>
                      {label}
                      <span style={{ marginLeft: 5, color: 'rgba(167,139,250,0.3)', fontWeight: 600 }}>
                        {cats.length}
                      </span>
                    </span>
                    <button
                      onClick={() => {
                        if (isAddingHere) { setInlineAdd(null); setInlineVal(''); }
                        else { setInlineAdd(sectionKey); setInlineVal(''); }
                      }}
                      style={{
                        background: 'none', border: 'none', padding: '2px 4px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                        color: isAddingHere ? 'rgba(167,139,250,0.9)' : 'rgba(167,139,250,0.5)',
                        fontSize: 10, fontWeight: 700, fontFamily: "'Syne', sans-serif",
                        borderRadius: 4, transition: 'color 0.12s',
                      }}
                    >
                      <Plus size={9} />
                      Add
                    </button>
                  </div>

                  {/* Inline add input */}
                  {isAddingHere && (
                    <div style={{
                      margin: '2px 16px 4px',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <input
                        ref={inlineRef}
                        value={inlineVal}
                        onChange={e => setInlineVal(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); commitInline(); }
                          if (e.key === 'Escape') { setInlineAdd(null); setInlineVal(''); }
                        }}
                        placeholder="Category name…"
                        style={{
                          flex: 1, padding: '5px 9px',
                          borderRadius: 7, fontSize: 12,
                          border: '1px solid rgba(139,92,246,0.45)',
                          background: 'rgba(139,92,246,0.08)',
                          color: '#f0f0f5', outline: 'none',
                          fontFamily: "'Syne', sans-serif",
                        }}
                      />
                      <button
                        onClick={commitInline}
                        disabled={!inlineVal.trim()}
                        style={{
                          padding: '5px 10px', borderRadius: 7,
                          background: inlineVal.trim() ? 'rgba(139,92,246,0.75)' : 'rgba(139,92,246,0.2)',
                          border: 'none', color: '#fff', fontSize: 11, fontWeight: 700,
                          cursor: inlineVal.trim() ? 'pointer' : 'not-allowed',
                          fontFamily: "'Syne', sans-serif",
                          transition: 'background 0.12s',
                        }}
                      >
                        Add
                      </button>
                    </div>
                  )}

                  {/* Category rows */}
                  {cats.length === 0 && !isAddingHere ? (
                    <div style={{
                      padding: '4px 16px 8px',
                      fontSize: 11, color: 'rgba(240,240,245,0.22)',
                      fontFamily: "'Syne', sans-serif",
                    }}>
                      No categories
                    </div>
                  ) : (
                    <div style={{ padding: '0 8px 4px' }}>
                      {cats.map((cat, idx) => {
                        const itemCount = sectionItems.filter(w => w.category?.toLowerCase() === cat.toLowerCase()).length;
                        const isLast = idx === cats.length - 1;
                        return (
                          <div
                            key={cat}
                            style={{
                              display: 'flex', alignItems: 'center',
                              padding: '0 8px',
                              borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
                              minHeight: 34,
                              gap: 6,
                            }}
                          >
                            {/* Name */}
                            <span style={{
                              flex: 1, fontSize: 12, fontWeight: 600, color: '#dcdce8',
                              fontFamily: "'Syne', sans-serif",
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {cat}
                            </span>

                            {/* Count badge */}
                            <span style={{
                              fontSize: 10, fontWeight: 600, letterSpacing: '0.01em',
                              color: itemCount === 0 ? 'rgba(240,240,245,0.2)' : 'rgba(167,139,250,0.55)',
                              fontFamily: "'Syne', sans-serif",
                              minWidth: 20, textAlign: 'right', flexShrink: 0,
                            }}>
                              {itemCount}
                            </span>

                            {/* Rename */}
                            <motion.button
                              whileHover={{ background: 'rgba(139,92,246,0.2)' }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => onRename(cat, sectionKey)}
                              title="Rename"
                              style={{
                                width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                                background: 'rgba(139,92,246,0.08)',
                                border: '1px solid rgba(139,92,246,0.15)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', transition: 'background 0.12s',
                              }}
                            >
                              <Pencil size={10} color="#a78bfa" />
                            </motion.button>

                            {/* Delete */}
                            <motion.button
                              whileHover={{ background: 'rgba(239,68,68,0.22)' }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => onDelete(cat, sectionKey)}
                              title="Delete"
                              style={{
                                width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                                background: 'rgba(239,68,68,0.07)',
                                border: '1px solid rgba(239,68,68,0.16)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', transition: 'background 0.12s',
                              }}
                            >
                              <Trash size={10} color="rgba(248,113,113,0.85)" />
                            </motion.button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Divider between sections */}
                  {sIdx < sections.length - 1 && (
                    <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 0' }} />
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
        </div>
      </>
    </AnimatePresence>
  );
};

// Signed positive modulo — mirrors the helper in SectionSwitcher.tsx, used
// here to compute the shortest cyclic delta's sign for content-swipe-driven
// section changes, and to derive each panel's rotating flex `order`.
const mod = (n: number, m: number) => ((n % m) + m) % m;

export function HomePage() {
  const { tokens } = useAppTheme();
  const { user, login, logout, loading } = useAuth() as AuthContextType;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Wrapper that closes all fixed overlays before invoking the wallet connect
  // flow. On Android (Mobile Wallet Adapter), the OS intent fires while the web
  // view is still mounted; any position:fixed element with pointer-events:auto
  // intercepts touches on the Android system "Allow" dialog that appears on top.
  // Dispatching 'poof:close-overlays' lets every overlay listener unmount itself
  // synchronously, then we defer login() to the next tick so React commits the
  // removal before the intent redirect happens.
  const handleLogin = () => {
    window.dispatchEvent(new Event('poof:close-overlays'));
    setTimeout(async () => {
      try {
        await login();
      } catch (error) {
        console.error('Failed to login', error);
      }
    }, 0);
  };

  // Prefetch all secondary route chunks while the browser is idle so that
  // navigating to a creator profile or legal page requires zero additional
  // network round-trips. Uses the same module factories as React.lazy so
  // Vite deduplicates the requests.
  useEffect(() => { prefetchAllRoutes(); }, []);

  // Restored from sessionStorage so that navigating to a creator profile (a real
  // route change that unmounts HomePage entirely) and pressing back — via either
  // the in-app chevron or Android hardware back — lands on the tab (Explore /
  // Library / Favorites / Sales / Profile) the user actually came from, instead
  // of always resetting to Explore. Mirrors the activeSection restoration below.
  const [activeTab, setActiveTab] = useState<Tab>(() => getStoredActiveTab() as Tab);

  // Persist the active tab on every change so it survives the profile-page
  // round trip described above.
  useEffect(() => {
    setStoredActiveTab(activeTab);
  }, [activeTab]);

  // ── Library multi-select (long-press to enter, tap to toggle, header trash
  // icon to bulk-delete) — lifted here since the trash action renders in the
  // page shell's header while selection state is driven from inside LibraryView.
  const [librarySelectionMode, setLibrarySelectionMode] = useState(false);
  const [librarySelectedIds, setLibrarySelectedIds] = useState<Set<string>>(new Set());

  const handleLibraryLongPressItem = useCallback((id: string) => {
    setLibrarySelectionMode(true);
    setLibrarySelectedIds(new Set([id]));
  }, []);

  const handleLibraryToggleSelectItem = useCallback((id: string) => {
    setLibrarySelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      if (next.size === 0) {
        setLibrarySelectionMode(false);
      }
      return next;
    });
  }, []);

  const handleLibraryExitSelection = useCallback(() => {
    setLibrarySelectionMode(false);
    setLibrarySelectedIds(new Set());
  }, []);

  // Android/browser back press exits selection mode instead of navigating away.
  useBackButtonClose(librarySelectionMode, handleLibraryExitSelection, 'library-selection');

  const handleLibraryBulkTrash = useCallback(async () => {
    if (!user || librarySelectedIds.size === 0) return;
    const ids = Array.from(librarySelectedIds);
    try {
      const ops = ids.map(id =>
        buildUsersTrash(user.address, id, {
          wallpaperId: id,
          owner: Address.publicKey(user.address),
          deletedAt: Time.Now,
        })
      );
      const result = await setMany(ops);
      if (!result) {
        haptic.error();
        notify.error('Failed to move items to trash', user.address);
        return;
      }
      haptic.medium();
      setLibrarySelectionMode(false);
      setLibrarySelectedIds(new Set());
    } catch {
      haptic.error();
      notify.error('Failed to move items to trash', user.address);
    }
  }, [user, librarySelectedIds]);

  const [selectedWallpaper, setSelectedWallpaper] = useState<WallpapersResponse | null>(null);
  // Ordered copy of the source list for the currently-open fullview session.
  // Set once on open, stays stable while the user swipes. Order matches the grid.
  const [shuffledViewerList, setShuffledViewerList] = useState<WallpapersResponse[] | null>(null);
  // True during the brief window of a ringtone↔wallpaper cross-viewer handoff.
  // Both viewers use this to skip their fade animations so no background flashes.
  const [viewerHandoffActive, setViewerHandoffActive] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [priceFilter, setPriceFilter] = useState<PriceFilter>('all');
  // Per-section "All" dropdown sort state for the Explore category rows — kept
  // independent per section so changing sort in one doesn't affect the others.
  const [exploreWallpaperFilterKey, setExploreWallpaperFilterKey] = useState<CategoryFilterKey>('all');
  const [exploreLiveFilterKey, setExploreLiveFilterKey] = useState<CategoryFilterKey>('all');
  const [exploreRingtoneFilterKey, setExploreRingtoneFilterKey] = useState<CategoryFilterKey>('all');
  const [exploreUsersFilterKey, setExploreUsersFilterKey] = useState<CategoryFilterKey>('all');
  // Search state — initialised from sessionStorage so that navigating to a
  // creator profile (route change) and pressing back restores the query.
  const [searchQuery, setSearchQuery] = useState(() => {
    try { return sessionStorage.getItem('seekscape-search-query') ?? ''; } catch { return ''; }
  });
  const [searchOpen, setSearchOpen] = useState(() => {
    try { return sessionStorage.getItem('seekscape-search-open') === 'true'; } catch { return false; }
  });
  // Persist search query to sessionStorage whenever it changes.
  useEffect(() => {
    try {
      if (searchQuery) {
        sessionStorage.setItem('seekscape-search-query', searchQuery);
      } else {
        sessionStorage.removeItem('seekscape-search-query');
      }
    } catch { /* ignore */ }
  }, [searchQuery]);
  // Persist search open state to sessionStorage whenever it changes.
  useEffect(() => {
    try {
      if (searchOpen) {
        sessionStorage.setItem('seekscape-search-open', 'true');
      } else {
        sessionStorage.removeItem('seekscape-search-open');
      }
    } catch { /* ignore */ }
  }, [searchOpen]);
  const handleSearchOpenChange = useCallback((open: boolean) => {
    setSearchOpen(open);
    // NOTE: query is NOT cleared here. Only the explicit X button in the
    // search input bar clears the query. Pressing back (or programmatic close
    // from inside the viewer flow) merely dismisses the results panel so the
    // user can return to search with the same query still loaded.
  }, []);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [balanceRefreshKey, setBalanceRefreshKey] = useState(0);
  const [showTrash, setShowTrash] = useState(false);
  const [showAddWallpaper, setShowAddWallpaper] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showArchiveCategory, setShowArchiveCategory] = useState(false);
  const [showSubscriptionsDialog, setShowSubscriptionsDialog] = useState(false);
  const [viewerSource, setViewerSource] = useState<'grid' | 'favorites' | 'library' | 'live' | 'ringtone'>('grid');
  const lastViewedWallpaperIdRef = useRef<string | null>(null);
  // When the WallpaperViewer is opened from the SeeAll modal, closing it should
  // restore the SeeAll modal instead of returning to the Explore page.
  const viewerReturnToSeeAllRef = useRef(false);
  // Stores the exact filtered list from SeeAll / SeeAllLive when the viewer is
  // opened from those modals. Used as wallpapersList so swipe-navigation stays
  // within the same category the user was browsing in the modal.
  const viewerFilteredListRef = useRef<WallpapersResponse[] | null>(null);
  // When the WallpaperViewer is opened from the search overlay, the search
  // overlay is left open underneath the viewer. Closing the viewer should
  // reveal the search overlay again (not clear it).
  const viewerOpenedFromSearchRef = useRef(false);
  // State-level mirror of viewerOpenedFromSearchRef used for reactive props
  // (e.g. suppressSearchBackClose passed to CategoryPageShell). The ref
  // remains the source of truth for the popstate handler (synchronous reads).
  const [viewerOpenedFromSearch, setViewerOpenedFromSearch] = useState(false);
  // Callback provided by CategoryPageShell to signal an intentional navigation
  // before unmount — prevents the back-button cleanup from calling history.back()
  // and undoing the navigation.
  const markSearchNavigatingRef = useRef<(() => void) | null>(null);

  // The search control now lives inline in the Explore category chip row
  // (relocated out of the header). searchChipInputRef points at that actual
  // <input> element; focusSearchInputRef exposes a stable focus() callback
  // used by "Try another search" flows to reopen the mobile keyboard.
  const searchChipInputRef = useRef<HTMLInputElement>(null);
  const focusSearchInputRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    focusSearchInputRef.current = () => searchChipInputRef.current?.focus();
  }, []);

  // See All modal state
  const [showSeeAllModal, setShowSeeAllModal] = useState(false);
  const [seeAllCategory, setSeeAllCategory] = useState(() => {
    try { return sessionStorage.getItem('seekscape-seeall-category') ?? 'All'; } catch { return 'All'; }
  });
  const [seeAllFilterKey, setSeeAllFilterKey] = useState<SeeAllFilterKey>(() => {
    try {
      const stored = sessionStorage.getItem('seekscape-seeall-filter');
      if (stored && ['all', 'new', 'popular', 'price_low', 'price_high'].includes(stored)) return stored as SeeAllFilterKey;
    } catch {}
    return 'all';
  });

  // See All Ringtones modal state
  const [showSeeAllRingtonesModal, setShowSeeAllRingtonesModal] = useState(false);
  const [seeAllRingtonesCategory, setSeeAllRingtonesCategory] = useState(() => {
    try { return sessionStorage.getItem('seekscape-seeall-ringtones-category') ?? 'All'; } catch { return 'All'; }
  });

  // See All Live Wallpapers modal state
  const [showSeeAllLiveModal, setShowSeeAllLiveModal] = useState(false);
  const [seeAllLiveCategory, setSeeAllLiveCategory] = useState(() => {
    try { return sessionStorage.getItem('seekscape-seeall-live-category') ?? 'All'; } catch { return 'All'; }
  });
  // When the WallpaperViewer is opened from the SeeAllLive modal, closing the viewer should restore it
  const viewerReturnToSeeAllLiveRef = useRef(false);

  const handleSetSeeAllCategory = (cat: string) => {
    setSeeAllCategory(cat);
    try { sessionStorage.setItem('seekscape-seeall-category', cat); } catch {}
  };
  const handleSetSeeAllFilterKey = (key: SeeAllFilterKey) => {
    setSeeAllFilterKey(key);
    try { sessionStorage.setItem('seekscape-seeall-filter', key); } catch {}
  };
  const handleSetSeeAllRingtonesCategory = (cat: string) => {
    setSeeAllRingtonesCategory(cat);
    try { sessionStorage.setItem('seekscape-seeall-ringtones-category', cat); } catch {}
  };

  const handleSetSeeAllLiveCategory = (cat: string) => {
    setSeeAllLiveCategory(cat);
    try { sessionStorage.setItem('seekscape-seeall-live-category', cat); } catch {}
  };

  // Nav visibility controlled by scroll in All Wallpapers / All Ringtones modals
  const [modalNavVisible, setModalNavVisible] = useState(true);

  // Grid column count preference — persisted to localStorage
  const [gridColumns, setGridColumns] = useState<number>(() => {
    try {
      const stored = localStorage.getItem('seekscape-grid-columns');
      if (stored !== null) {
        const parsed = parseInt(stored, 10);
        if ([2, 3, 4, 5].includes(parsed)) return parsed;
      }
    } catch {}
    return 2;
  });

  const handleGridColumnsChange = useCallback((val: number) => {
    setGridColumns(val);
    try {
      localStorage.setItem('seekscape-grid-columns', String(val));
    } catch {}
  }, []);

  // Sort order preference — persisted to localStorage; null = neutral/random
  const [sortOrder, setSortOrder] = useState<SortState>(() => {
    try {
      const stored = localStorage.getItem('seekscape-sort-order');
      if (stored === 'newest' || stored === 'oldest' || stored === 'popular') return stored as SortOrder;
    } catch {}
    return null;
  });

  const handleSortChange = useCallback((order: SortOrder) => {
    setSortOrder(prev => {
      const next = prev === order ? null : order;
      try {
        if (next === null) localStorage.removeItem('seekscape-sort-order');
        else localStorage.setItem('seekscape-sort-order', next);
      } catch {}
      return next;
    });
  }, []);

  // Wallpaper categories from DB (admin-managed, real-time). Used instead of localStorage.
  const { categories: persistentCategories } = useWallpaperCategoriesFromDB();

  // Setter that writes wallpaper categories to the DB. Mirrors the old setPersistentCategories
  // setState pattern so the rest of the handlers can use it without changing shape.
  const setPersistentCategories = async (updater: ((prev: string[]) => string[]) | string[]) => {
    const next = typeof updater === 'function' ? updater(persistentCategories) : updater;
    await saveWallpaperCategoriesToDB(next);
  };

  // Persistent custom categories for live-wallpaper and ringtone types
  const [persistentLiveWallpaperCategories, setPersistentLiveWallpaperCategories] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('seekscape-live-wallpaper-categories');
      if (stored) return JSON.parse(stored) as string[];
    } catch {}
    return [];
  });

  useEffect(() => {
    try {
      localStorage.setItem('seekscape-live-wallpaper-categories', JSON.stringify(persistentLiveWallpaperCategories));
    } catch {}
  }, [persistentLiveWallpaperCategories]);

  const [persistentRingtoneCategories, setPersistentRingtoneCategories] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('seekscape-ringtone-categories');
      if (stored) return JSON.parse(stored) as string[];
    } catch {}
    return [];
  });

  useEffect(() => {
    try {
      localStorage.setItem('seekscape-ringtone-categories', JSON.stringify(persistentRingtoneCategories));
    } catch {}
  }, [persistentRingtoneCategories]);

  const handleNewCategory = useCallback(async (mediaType: 'wallpaper' | 'live-wallpaper' | 'ringtone', category: string) => {
    const trimmed = category.trim();
    if (!trimmed) return;
    if (mediaType === 'wallpaper') {
      if (!persistentCategories.includes(trimmed)) {
        await setPersistentCategories([...persistentCategories, trimmed]);
      }
    } else if (mediaType === 'live-wallpaper') {
      setPersistentLiveWallpaperCategories(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
    } else if (mediaType === 'ringtone') {
      setPersistentRingtoneCategories(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
    }
  }, [persistentCategories]);

  // Category admin context menu
  const [categoryMenu, setCategoryMenu] = useState<{ cat: string; x: number; y: number; section?: CategorySection } | null>(null);
  const [renameDialog, setRenameDialog] = useState<{ cat: string; section?: CategorySection } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [clearConfirm, setClearConfirm] = useState<{ cat: string; section?: CategorySection } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ cat: string; section?: CategorySection } | null>(null);
  const [categoryActionLoading, setCategoryActionLoading] = useState(false);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [showMyUploads, setShowMyUploads] = useState(false);
  const [showUserUploadModal, setShowUserUploadModal] = useState(false);
  const [showModeration, setShowModeration] = useState(false);
  const [moderationFullViewOpen, setModerationFullViewOpen] = useState(false);
  const [showProfileSetupGate, setShowProfileSetupGate] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const SORT_ORDER_CYCLE_HOME: SortOrder[] = ['newest', 'oldest', 'popular'];

  const isAdmin = user?.address === ADMIN_ADDRESS || user?.address === 'HKbZbRR7jWWR5VRN8KFjvTCHEzJQgameYxKQxh2gPoof';

  // ── Section switcher (Wallpapers / Live / Ringtones / By Users) ──
  // Index 0 = Wallpapers, 1 = Live, 2 = Ringtones, 3 = By Users.
  // Logged-out "Preview" mode is locked to the Wallpapers section (index 0, trending
  // row only) — see the preview lock effect and handleSectionIndexChange below.
  const WALLPAPERS_SECTION_INDEX = 0;
  // Restored from sessionStorage so that navigating to a creator profile (a real
  // route change that unmounts HomePage entirely) and pressing back lands on the
  // section the user was actually on, instead of always resetting to Wallpapers.
  // A stored value only ever gets written while logged in (see the persist effect
  // below and handleSectionIndexChange's early-return), so it can be trusted on
  // its own — we must NOT gate this on first-render `user`, since useAuth() always
  // mounts with `user === null` and only syncs the real value in a later effect,
  // which would otherwise force Wallpapers on every HomePage remount.
  const initialActiveSection = getStoredActiveSection(SECTIONS.length);
  const [activeSection, setActiveSection] = useState(initialActiveSection);

  // Persist the active section on every change so it survives the profile-page
  // round trip described above.
  useEffect(() => {
    setStoredActiveSection(activeSection);
  }, [activeSection]);

  // ── Content-track direction state ─────────────────────────────────────
  // Mirrors the title ribbon's signed-step semantics for the swipeable
  // content track below (see the flex `order` + translateX math at the
  // track render site). `contentPivot` is the section whose flex `order`
  // is currently treated as centered/rank-1; it stays on the OUTGOING
  // section for the duration of a slide so the correct forward/backward
  // neighbor (already sitting one order-slot away) is what the transform
  // shift reveals, then snaps onto the new section (instantly, no
  // transition) once the slide settles. `contentShift` is the signed
  // ±1 applied on top of the resting position while a slide is in flight.
  const CONTENT_SLIDE_MS = 330; // matches the 0.32s CSS transition below + settle buffer
  // Must start in sync with the (possibly restored) activeSection above — the
  // per-panel visibility check below hides a panel only when BOTH activeSection
  // and contentPivot point away from it, so an out-of-sync pivot on a restored
  // non-Wallpapers section would leave two panels visible at once on first paint.
  const [contentPivot, setContentPivot] = useState(initialActiveSection);
  const [contentShift, setContentShift] = useState<0 | 1 | -1>(0);
  const [contentTransitionEnabled, setContentTransitionEnabled] = useState(true);
  const contentSettleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep Preview (logged-out) locked to the Wallpapers section, and reset back to
  // Wallpapers on login/logout so the app always opens on Wallpapers. Not a
  // user-driven swipe/tap, so the content track snaps directly (no slide) —
  // clear any in-flight transition state and re-pivot instantly.
  //
  // useAuth() always mounts with `user === null` and `loading === true`, then
  // settles to the real user in a later effect. That null→user settle on every
  // HomePage remount looks identical to a genuine login, so we must not treat
  // it as one (it previously stomped the restored section back to Wallpapers on
  // every "back" navigation). `authHydratedRef` records the first settle
  // (loading flips to false) as the baseline instead of a transition — only
  // reads of `user` AFTER that baseline can trigger the reset.
  const wasLoggedInRef = useRef<boolean>(!!user);
  const authHydratedRef = useRef<boolean>(false);
  useEffect(() => {
    if (loading) return;
    if (!authHydratedRef.current) {
      authHydratedRef.current = true;
      wasLoggedInRef.current = !!user;
      return;
    }
    const wasLoggedIn = wasLoggedInRef.current;
    const isLoggedIn = !!user;
    if (wasLoggedIn !== isLoggedIn) {
      if (contentSettleTimeoutRef.current) {
        clearTimeout(contentSettleTimeoutRef.current);
        contentSettleTimeoutRef.current = null;
      }
      setContentTransitionEnabled(false);
      setContentPivot(WALLPAPERS_SECTION_INDEX);
      setContentShift(0);
      setActiveSection(WALLPAPERS_SECTION_INDEX);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setContentTransitionEnabled(true));
      });
    }
    wasLoggedInRef.current = isLoggedIn;
  }, [user, loading]);

  // Ignore section-switcher / swipe-gesture index changes while logged out so
  // Preview mode can't be swiped away from the Wallpapers trending row.
  //
  // `delta` (optional) is the ribbon's own signed step — positive glides
  // right→left (forward, including the 3→0 wrap), negative glides
  // left→right (backward, including the 0→3 wrap). When present (ribbon
  // taps/swipes) its sign drives the content slide direction so the two
  // mechanisms always agree, even for an N-slot tap on a non-adjacent
  // title. When absent (content-area swipe via useTabSwipe, always a
  // single adjacent step) the sign is derived from the shortest cyclic
  // delta, which is unambiguous for a single step.
  const handleSectionIndexChange = useCallback((index: number, delta?: number) => {
    if (!user) return;
    if (index === activeSection) return;
    const count = SECTIONS.length;
    let sign: 1 | -1;
    if (delta !== undefined && delta !== 0) {
      sign = delta > 0 ? 1 : -1;
    } else {
      let d = mod(index - activeSection, count);
      if (d > count / 2) d -= count;
      sign = d < 0 ? -1 : 1;
    }
    if (contentSettleTimeoutRef.current) {
      clearTimeout(contentSettleTimeoutRef.current);
      contentSettleTimeoutRef.current = null;
    }
    setContentPivot(activeSection);
    setContentTransitionEnabled(true);
    setContentShift(sign);
    setActiveSection(index);
    contentSettleTimeoutRef.current = setTimeout(() => {
      // Re-pivot onto the new section and reset the shift in the same
      // transition-free commit so the panel that just slid into view
      // doesn't visibly move.
      setContentTransitionEnabled(false);
      setContentPivot(index);
      setContentShift(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setContentTransitionEnabled(true));
      });
      contentSettleTimeoutRef.current = null;
    }, CONTENT_SLIDE_MS);
  }, [user, activeSection]);

  // Each real section's rotating flex `order` — recomputed from the current
  // pivot every render so the pivot section always sits at rank 1 (center),
  // its forward neighbor at rank 2, backward neighbor at rank 0, and the
  // opposite section at rank 3. See the track render site for how this
  // combines with `contentShift` to produce a correct-direction,
  // single-panel-width slide for every step, cyclic both ways.
  const contentOrder = useCallback(
    (sectionIdx: number) => mod(sectionIdx - contentPivot + 1, SECTIONS.length) - 1,
    [contentPivot]
  );

  const sectionColRefs = useRef<(HTMLDivElement | null)[]>([]);

  const currentAccentColor = SECTIONS[activeSection]?.accentColor ?? SECTIONS[0].accentColor;

  // ── Explore mode (admin-controlled via siteSettings/exploreMode) ──────────
  const { data: exploreModeDoc, loading: exploreModeLoading } = useRealtimeData<SiteSettingsResponse | null>(
    subscribeSiteSettings,
    true,
    'exploreMode',
  );
  const exploreMode: 'all-only' | 'tabs-only' =
    exploreModeLoading || !exploreModeDoc
      ? 'tabs-only'
      : (exploreModeDoc.value === 'all-only' ? 'all-only' : 'tabs-only');

  // (exploreMode is kept for the admin toggle UI but no longer affects activeSection)

  const handleToggleExploreMode = useCallback(async () => {
    const newMode = exploreMode === 'all-only' ? 'tabs-only' : 'all-only';
    await setSiteSettings('exploreMode', { value: newMode, updatedAt: Time.Now });
  }, [exploreMode]);

  // Skip the initial mount run of the activeSection reset effect so that
  // sessionStorage-restored search state isn't cleared on first render.
  const activeSectionMountedRef = useRef(false);

  // Reset filter/search state when switching sections. Deferred one frame so
  // this state churn (which drives heavy memoized wallpaper-list recomputes)
  // lands after the section-switch transition has started, instead of inside
  // the same commit/frame as the transition.
  useEffect(() => {
    if (!activeSectionMountedRef.current) {
      activeSectionMountedRef.current = true;
      return; // skip initial mount — don't clear sessionStorage-restored search state
    }
    const id = requestAnimationFrame(() => {
      setActiveCategory('All');
      setPriceFilter('all');
      setSearchQuery('');
      setSearchOpen(false);
      try { sessionStorage.removeItem('seekscape-search-query'); sessionStorage.removeItem('seekscape-search-open'); } catch { /* ignore */ }
      setShowSeeAllModal(false);
      setShowSeeAllRingtonesModal(false);
      setShowSeeAllLiveModal(false);
    });
    return () => cancelAnimationFrame(id);
  }, [activeSection]);

  // ── Swipe gesture — reuses the same useTabSwipe hook as Favorites/Profile ──
  const { swipeCallbackRef } = useTabSwipe({
    count: SECTIONS.length,
    activeIndex: activeSection,
    onIndexChange: handleSectionIndexChange,
    // Preview (logged-out) is locked to Wallpapers — the gesture stays fully inert
    // so it can't swallow vertical scroll either.
    enabled: !!user,
    // Wrap at the edges so the page body matches the endless top ribbon
    loop: true,
  });


  const skrBalance = useSkrBalance(user?.address, balanceRefreshKey);

  // Subscribe to the current user's profile to check if they have a username
  const { data: currentUserProfile } = useRealtimeData<UserProfilesResponse | null>(
    subscribeUserProfiles,
    !!user?.address,
    user?.address ?? '',
  );

  // Subscribe to the current user's avatar to check completeness
  const { data: currentUserAvatar } = useRealtimeData<UserAvatarsResponse | null>(
    subscribeUserAvatars,
    !!user?.address,
    user?.address ?? '',
  );

  const userProfileUsername = currentUserProfile?.username ?? null;
  const userProfileComplete = !!(currentUserProfile?.username) && !!(currentUserAvatar?.avatarUrl);

  // Subscribe to all user profiles for creator search results
  const { data: allUserProfiles } = useRealtimeData<UserProfilesResponse[]>(
    subscribeManyUserProfiles,
    true,
  );

  // Subscribe to all user avatars for the "By Users" creators grid
  const { data: allUserAvatars } = useRealtimeData<UserAvatarsResponse[]>(
    subscribeManyUserAvatars,
    true,
  );

  // Build avatarUrl map: walletAddress → avatarUrl
  const avatarUrlByWallet = useMemo(() => {
    const map: Record<string, string> = {};
    for (const a of allUserAvatars ?? []) {
      if (a.id && a.avatarUrl) map[a.id] = a.avatarUrl;
    }
    return map;
  }, [allUserAvatars]);

  // Shuffled creators list — shuffle once on mount (stable), then append new arrivals
  const [shuffledCreatorIds, setShuffledCreatorIds] = useState<string[]>([]);
  const seenCreatorIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const incoming = allUserProfiles ?? [];
    const newIds = incoming.filter(p => p.id && !seenCreatorIdsRef.current.has(p.id));
    if (newIds.length === 0) return;
    if (seenCreatorIdsRef.current.size === 0) {
      // First load — shuffle the entire list
      const shuffled = shuffleArray(incoming.map(p => p.id));
      for (const id of shuffled) seenCreatorIdsRef.current.add(id);
      setShuffledCreatorIds(shuffled);
    } else {
      // Subsequent updates — append new arrivals at the end in arrival order
      const toAdd = newIds.map(p => p.id);
      for (const id of toAdd) seenCreatorIdsRef.current.add(id);
      setShuffledCreatorIds(prev => [...prev, ...toAdd]);
    }
  }, [allUserProfiles]);

  // Build a fast lookup map: id → profile
  const userProfileById = useMemo(() => {
    const map: Record<string, UserProfilesResponse> = {};
    for (const p of allUserProfiles ?? []) {
      if (p.id) map[p.id] = p;
    }
    return map;
  }, [allUserProfiles]);

  // "By Users" Explore row sort — newest-joined creators first when selected via
  // the CategoryChipsRow "All" dropdown; otherwise the existing stable shuffle order.
  const sortedCreatorIds = useMemo(() => {
    if (exploreUsersFilterKey !== 'new') return shuffledCreatorIds;
    return [...shuffledCreatorIds].sort((a, b) => {
      const bCreatedAt = userProfileById[b]?.tarobase_created_at ?? 0;
      const aCreatedAt = userProfileById[a]?.tarobase_created_at ?? 0;
      return bCreatedAt - aCreatedAt;
    });
  }, [shuffledCreatorIds, exploreUsersFilterKey, userProfileById]);

  // Subscribe to unread notifications for bell badge
  const { data: unreadNotifications } = useRealtimeData<NotificationsItemsResponse[]>(
    subscribeManyNotificationsItems,
    !!user?.address,
    user?.address ?? '',
    "read = false",
  );
  const unreadNotificationCount = (unreadNotifications ?? []).length;

  // Handler: open My Uploads — gated behind profile completeness
  const handleOpenMyUploads = useCallback(() => {
    if (!userProfileComplete) {
      setShowProfileSetupGate(true);
    } else {
      setShowMyUploads(true);
    }
  }, [userProfileComplete]);

  // Ringtone player context — used to open the full-screen player when a ringtone is tapped
  const ringtonePlayer = useRingtonePlayer();

  // Scroll restoration for every Explore surface (Wallpapers / Live / Ringtones),
  // plus Favorites and Library — they all render inside this same window-scrolled
  // page and share these two full-view overlays, so one instance covers all of
  // them: closing WallpaperViewer or the ringtone full-screen player always snaps
  // the page back to the exact offset it was at before the overlay opened.
  useScrollRestoration(selectedWallpaper !== null || ringtonePlayer.fullScreenOpen);

  // Pause + hide the floating mini player whenever a detail/sub-screen overlay opens
  // (it must not sit on top of the overlay). When the overlay closes, the floating
  // mini player becomes visible again in paused state.
  // Tab switching does NOT trigger this — only entering a detail screen does.
  useEffect(() => {
    const anyDetailOpen =
      selectedWallpaper !== null ||
      showSeeAllModal ||
      showSeeAllRingtonesModal ||
      showSeeAllLiveModal ||
      showTrash ||
      showAddWallpaper ||
      showTransactions ||
      showArchiveCategory ||
      showManageCategories ||
      renameDialog !== null ||
      clearConfirm !== null ||
      deleteConfirm !== null;

    if (anyDetailOpen) {
      ringtonePlayer.pauseAndHideFloating();
    } else {
      ringtonePlayer.showFloating();
    }
  }, [
    selectedWallpaper,
    showSeeAllModal,
    showSeeAllRingtonesModal,
    showSeeAllLiveModal,
    showTrash,
    showAddWallpaper,
    showTransactions,
    showArchiveCategory,
    showManageCategories,
    renameDialog,
    clearConfirm,
    deleteConfirm,
  ]);

  // Android hardware back button support via popstate.
  // Priority order (highest first):
  //   ringtone full-screen → upload modal → my-uploads →
  //   rename/clear/delete dialogs → manage-categories →
  //   add-wallpaper → transactions → moderation → profile-setup-gate →
  //   see-all ringtones → see-all wallpapers → wallpaper viewer →
  //   trash → tab navigation → stay on home (never exit).
  // Tracks the tab the user came FROM so back restores it correctly.
  const prevTabRef = useRef<Tab>('home');

  useEffect(() => {
    // Push a sentinel so the very first back press fires popstate instead of exiting.
    window.history.pushState({ page: 'sub' }, '');

    const handlePopState = () => {
      // Programmatic history.back() from useBackButtonClose cleanup (an overlay was
      // closed via in-app UI, not a real back press). HomePage's own sentinel is
      // still beneath, so stay completely passive — no state change, no push.
      if (isCleanupBackInFlight()) return;
      // ── Highest priority: ringtone full-screen player ────────────────────
      if (ringtonePlayer.fullScreenOpen) {
        ringtonePlayer.closeFullScreen();
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Upload modal (must close before my-uploads panel) ───────────────
      if (showUserUploadModal) {
        setShowUserUploadModal(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── My Uploads panel ────────────────────────────────────────────────
      if (showMyUploads) {
        setShowMyUploads(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Category rename / clear / delete dialogs ─────────────────────────
      if (renameDialog) {
        setRenameDialog(null);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      if (clearConfirm) {
        setClearConfirm(null);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      if (deleteConfirm) {
        setDeleteConfirm(null);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Manage Categories sheet ──────────────────────────────────────────
      if (showManageCategories) {
        setShowManageCategories(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Admin: Add Wallpaper ─────────────────────────────────────────────
      if (showAddWallpaper) {
        setShowAddWallpaper(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Admin: Transactions modal ────────────────────────────────────────
      if (showTransactions) {
        setShowTransactions(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Admin: Archive Category modal ────────────────────────────────────
      if (showArchiveCategory) {
        setShowArchiveCategory(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Admin: Moderation panel ──────────────────────────────────────────
      if (showModeration) {
        setShowModeration(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Profile setup gate ───────────────────────────────────────────────
      if (showProfileSetupGate) {
        setShowProfileSetupGate(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── See All Live Wallpapers modal ────────────────────────────────────
      if (showSeeAllLiveModal) {
        setShowSeeAllLiveModal(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── See All Ringtones modal ──────────────────────────────────────────
      if (showSeeAllRingtonesModal) {
        setShowSeeAllRingtonesModal(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── See All Wallpapers modal ─────────────────────────────────────────
      if (showSeeAllModal) {
        setShowSeeAllModal(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Wallpaper viewer ─────────────────────────────────────────────────
      if (selectedWallpaper) {
        // Explore / Favorites / Library scroll position is restored automatically
        // by useScrollRestoration (keyed on selectedWallpaper !== null) — no
        // manual scroll handling needed here.
        setSelectedWallpaper(null);
        setShuffledViewerList(null);
        if (viewerOpenedFromSearchRef.current) {
          // Opened from search — search overlay is still mounted underneath;
          // just clear the flag and let it reappear. Do not clear searchQuery.
          viewerOpenedFromSearchRef.current = false;
          setViewerOpenedFromSearch(false);
          // The search overlay's history sentinel ({ overlay: 'header-search' })
          // is now the top of the stack. Do NOT push a new sentinel here — the
          // search sentinel already serves that role and will close search on
          // the next back press via useBackButtonClose in CategoryPageShell.
          return;
        } else if (viewerReturnToSeeAllRef.current) {
          viewerReturnToSeeAllRef.current = false;
          setShowSeeAllModal(true);
        } else if (viewerReturnToSeeAllLiveRef.current) {
          viewerReturnToSeeAllLiveRef.current = false;
          setShowSeeAllLiveModal(true);
        }
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Trash view ───────────────────────────────────────────────────────
      if (showTrash) {
        setShowTrash(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Subscriptions dialog ─────────────────────────────────────────────
      if (showSubscriptionsDialog) {
        setShowSubscriptionsDialog(false);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // A useBackButtonClose overlay (e.g. Library multi-select) is open and its own
      // handler already consumed this back press. Never also switch tabs.
      if (hasActiveOverlays()) {
        window.history.pushState({ page: 'sub' }, '');
        return;
      }
      // ── Tab navigation: go back to the previous tab ──────────────────────
      if (activeTab !== 'home') {
        // Restore the tab the user navigated from (default: home).
        const returnTab = prevTabRef.current;
        prevTabRef.current = 'home';
        setActiveTab(returnTab);
        window.history.pushState({ page: 'sub' }, '');
        return;
      }

      // ── At root (home tab, no overlays open). Always push sentinel — never exit. ──
      // The app must never close when the user is on Explore. Push a new sentinel
      // so the next back press fires popstate again and we stay on Explore.
      window.history.pushState({ page: 'sub' }, '');
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [
    ringtonePlayer.fullScreenOpen,
    ringtonePlayer.closeFullScreen,
    showUserUploadModal,
    showMyUploads,
    renameDialog,
    clearConfirm,
    deleteConfirm,
    showManageCategories,
    showAddWallpaper,
    showTransactions,
    showArchiveCategory,
    showModeration,
    showProfileSetupGate,
    showSeeAllLiveModal,
    showSeeAllRingtonesModal,
    showSeeAllModal,
    selectedWallpaper,
    showTrash,
    showSubscriptionsDialog,
    activeTab,
    viewerSource,
  ]);

  // Push a history entry when the ringtone full-screen player opens so the
  // Android hardware back button has something to pop (the popstate handler
  // above will close the overlay when that entry is popped).
  useEffect(() => {
    if (ringtonePlayer.fullScreenOpen) {
      window.history.pushState({ page: 'sub' }, '');
    }
  }, [ringtonePlayer.fullScreenOpen]);

  // Push a history entry when the "See All" wallpapers modal opens.
  useEffect(() => {
    if (showSeeAllModal) {
      window.history.pushState({ page: 'sub' }, '');
    }
  }, [showSeeAllModal]);

  // Push a history entry when the "See All" ringtones modal opens.
  useEffect(() => {
    if (showSeeAllRingtonesModal) {
      window.history.pushState({ page: 'sub' }, '');
    }
  }, [showSeeAllRingtonesModal]);

  // Push a history entry when the "See All" live wallpapers modal opens.
  useEffect(() => {
    if (showSeeAllLiveModal) {
      window.history.pushState({ page: 'sub' }, '');
    }
  }, [showSeeAllLiveModal]);

  // Push a history entry when MyUploadsPanel opens.
  useEffect(() => {
    if (showMyUploads) {
      window.history.pushState({ page: 'sub' }, '');
    }
  }, [showMyUploads]);

  // Push a history entry when UserUploadModal opens.
  useEffect(() => {
    if (showUserUploadModal) {
      window.history.pushState({ page: 'sub' }, '');
    }
  }, [showUserUploadModal]);

  // Push history entries for other overlays that need back-button support.
  useEffect(() => {
    if (showAddWallpaper) window.history.pushState({ page: 'sub' }, '');
  }, [showAddWallpaper]);

  useEffect(() => {
    if (showTransactions) window.history.pushState({ page: 'sub' }, '');
  }, [showTransactions]);

  useEffect(() => {
    if (showArchiveCategory) window.history.pushState({ page: 'sub' }, '');
  }, [showArchiveCategory]);

  useEffect(() => {
    if (showManageCategories) window.history.pushState({ page: 'sub' }, '');
  }, [showManageCategories]);

  useEffect(() => {
    if (showModeration) window.history.pushState({ page: 'sub' }, '');
  }, [showModeration]);

  useEffect(() => {
    if (showProfileSetupGate) window.history.pushState({ page: 'sub' }, '');
  }, [showProfileSetupGate]);

  useEffect(() => {
    if (showTrash) window.history.pushState({ page: 'sub' }, '');
  }, [showTrash]);

  useEffect(() => {
    if (showSubscriptionsDialog) window.history.pushState({ page: 'sub' }, '');
  }, [showSubscriptionsDialog]);

  const { data: allWallpapers } = useRealtimeData<WallpapersResponse[]>(
    subscribeManyWallpapers,
    true
  );

  // Deep-link: if the URL has ?open=<wallpaperId> (e.g. from a notification tap),
  // auto-open that item in the viewer once the wallpapers list is available.
  // We track the last-attempted ID so we don't open the same item repeatedly.
  const autoOpenAttemptedRef = useRef<string | null>(null);
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || !allWallpapers || allWallpapers.length === 0) return;
    // Only attempt once per unique ID even if the effect re-runs
    if (autoOpenAttemptedRef.current === openId) return;
    autoOpenAttemptedRef.current = openId;

    const item = allWallpapers.find(w => w.id === openId && w.status !== 'deleted');
    if (item) {
      // Determine the relevant source list by media type and shuffle it once
      const mediaType = resolveMediaType(item);
      const sourceForDeepLink = mediaType === 'live-wallpaper'
        ? allWallpapers.filter(w => { const s = (w as any).status; return resolveMediaType(w) === 'live-wallpaper' && (!s || s === 'published') && w.hidden !== true; })
        : mediaType === 'ringtone'
        ? allWallpapers.filter(w => { const s = (w as any).status; return resolveMediaType(w) === 'ringtone' && (!s || s === 'published') && w.hidden !== true; })
        : allWallpapers.filter(w => { const s = (w as any).status; return resolveMediaType(w) === 'wallpaper' && (!s || s === 'published') && w.hidden !== true; });
      setShuffledViewerList([...sourceForDeepLink]);
      setSelectedWallpaper(item);
      // Remove the ?open= param from the URL so back-navigation is clean
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('open');
        return next;
      }, { replace: true });
    } else {
      // Item not found or deleted — show a toast and clean up the URL
      toast.error('Контент больше недоступен');
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('open');
        return next;
      }, { replace: true });
    }
  }, [searchParams, allWallpapers, setSearchParams]);

  const { data: userPurchases } = useRealtimeData<UsersPurchasesResponse[]>(
    subscribeManyUsersPurchases,
    !!user?.address,
    user?.address ?? ''
  );

  const { data: userFavorites } = useRealtimeData<UsersFavoritesResponse[]>(
    subscribeManyUsersFavorites,
    !!user?.address,
    user?.address ?? ''
  );

  const { data: userTrash } = useRealtimeData<UsersTrashResponse[]>(
    subscribeManyUsersTrash,
    !!user?.address,
    user?.address ?? ''
  );

  // Real-time subscriptions list for the current user
  const { data: userSubscriptions } = useRealtimeData<SubscriptionsCreatorsResponse[]>(
    subscribeManySubscriptionsCreators,
    !!user?.address,
    user?.address ?? ''
  );
  const subscriptionCount = (userSubscriptions ?? []).length;

  // ── Creator stats for Profile menu ───────────────────────────────────────
  // Count of wallpapers this user has uploaded that are publicly visible (published & not hidden)
  const myUploadCount = useMemo(() => {
    if (!user?.address || !allWallpapers) return 0;
    return (allWallpapers).filter(w =>
      w.uploadedBy?.toLowerCase() === user.address.toLowerCase() &&
      w.status === 'published' &&
      w.hidden !== true
    ).length;
  }, [allWallpapers, user?.address]);

  // Sales as a creator — subscribe to purchases where creator === user.address
  const { data: creatorSales } = useRealtimeData<PurchasesResponse[]>(
    subscribeManyPurchases,
    !!user?.address,
    user?.address ? `where creator = '${user.address}'` : '',
  );

  const salesSkrTotal = useMemo(() => {
    if (!creatorSales || creatorSales.length === 0) return '0';
    const isAdminCreator = user?.address === ADMIN_ADDRESS;
    const share = isAdminCreator ? 0.8 : 0.7;
    const totalRaw = creatorSales.reduce((sum, p) => sum + Math.floor((p.amt ?? 0) * share), 0);
    const totalHuman = totalRaw / 1_000_000;
    return totalHuman % 1 === 0
      ? totalHuman.toLocaleString()
      : totalHuman.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, [creatorSales, user?.address]);

  const purchasedIds = useMemo(() => {
    const s = new Set<string>();
    (userPurchases ?? []).forEach(p => s.add(p.wallpaperId));
    return s;
  }, [userPurchases]);

  // Optimistic favorite state: track pending adds/removes locally so the UI
  // reacts instantly without waiting for the subscription to refresh.
  const [optimisticFavAdded, setOptimisticFavAdded] = useState<Set<string>>(new Set());
  const [optimisticFavRemoved, setOptimisticFavRemoved] = useState<Set<string>>(new Set());

  const favoritedIds = useMemo(() => {
    const s = new Set<string>();
    (userFavorites ?? []).forEach(f => s.add(f.wallpaperId));
    // Apply optimistic overrides
    optimisticFavAdded.forEach(id => s.add(id));
    optimisticFavRemoved.forEach(id => s.delete(id));
    return s;
  }, [userFavorites, optimisticFavAdded, optimisticFavRemoved]);

  // Clear optimistic state once the subscription confirms the changes,
  // preventing double-counting in favoritedIds.
  useEffect(() => {
    const confirmedFavIds = new Set((userFavorites ?? []).map(f => f.wallpaperId));

    setOptimisticFavAdded(prev => {
      const next = new Set(prev);
      for (const id of prev) {
        if (confirmedFavIds.has(id)) next.delete(id);
      }
      return next.size !== prev.size ? next : prev;
    });

    setOptimisticFavRemoved(prev => {
      const next = new Set(prev);
      for (const id of prev) {
        if (!confirmedFavIds.has(id)) next.delete(id);
      }
      return next.size !== prev.size ? next : prev;
    });
  }, [userFavorites]);

  const trashedIds = useMemo(() => {
    const s = new Set<string>();
    (userTrash ?? []).forEach(t => s.add(t.wallpaperId));
    return s;
  }, [userTrash]);

  // Keep the ringtone player context in sync with the live purchased/favorited
  // state for the currently-loaded track. Without this, deleting a purchase
  // (e.g., permanent trash removal) leaves the player thinking the user still
  // owns the item, hiding the Buy button until reload.
  useEffect(() => {
    const id = ringtonePlayer.currentRingtone?.id;
    if (!id) return;
    ringtonePlayer.setPurchased(purchasedIds.has(id));
    ringtonePlayer.setFavorited(favoritedIds.has(id));
  }, [ringtonePlayer.currentRingtone?.id, purchasedIds, favoritedIds, ringtonePlayer.setPurchased, ringtonePlayer.setFavorited]);

  const filteredWallpapers = useMemo(() => {
    const list = allWallpapers ?? [];
    return list.filter(w => {
      // Section 0 (wallpapers): only items resolved as 'wallpaper' — exclude live, ringtones, icons
      if (resolveMediaType(w) !== 'wallpaper') return false;

      // Only show published, non-hidden items in the public catalog (legacy items without status pass through)
      const wStatus = (w as any).status;
      if (wStatus && wStatus !== 'published') return false;
      if (w.hidden === true) return false;

      const matchesCat = activeCategory === 'All' || w.category?.toLowerCase() === activeCategory.toLowerCase();
      const matchesSearch = searchQuery.trim().length < 1 || w.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPrice = (() => {
        if (priceFilter === 'all') return true;
        if (priceFilter === 'new') {
          const createdAt = w.createdAt ?? w.tarobase_created_at ?? 0;
          const sevenDaysAgo = Math.floor(Date.now() / 1000) - 7 * 24 * 60 * 60;
          return createdAt >= sevenDaysAgo;
        }
        const selectedOption = PRICE_FILTER_OPTIONS.find(option => option.value === priceFilter);
        return w.price === selectedOption?.price;
      })();
      return matchesCat && matchesSearch && matchesPrice;
    });
  }, [allWallpapers, activeCategory, searchQuery, priceFilter]);

  // Items for sections 1–3: filter by resolved mediaType (uses URL fallback for legacy items)
  const liveWallpaperItems = useMemo(() => {
    const matchesCat = activeCategory === 'All' || activeCategory === '';
    const matchesCatFn = (w: WallpapersResponse) => matchesCat || w.category?.toLowerCase() === activeCategory.toLowerCase();
    const matchesSearchFn = (w: WallpapersResponse) => searchQuery.trim().length < 1 || (w.title ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    const isPublished = (w: WallpapersResponse) => { const s = (w as any).status; return (!s || s === 'published') && w.hidden !== true; };
    const filtered = (allWallpapers ?? [])
      .filter(w => resolveMediaType(w) === 'live-wallpaper' && isPublished(w) && matchesCatFn(w) && matchesSearchFn(w));

    // Session-stable shuffled order (see src/utils/session-shuffle.ts) — same
    // seed as the Wallpapers and Ringtones sections, so all three Explore
    // sections reshuffle together only on a full app restart.
    return sessionShuffle(filtered);
  }, [allWallpapers, activeCategory, searchQuery]);

  const ringtoneItems = useMemo(() => {
    const matchesCat = activeCategory === 'All' || activeCategory === '';
    const matchesCatFn = (w: WallpapersResponse) => matchesCat || w.category?.toLowerCase() === activeCategory.toLowerCase();
    const matchesSearchFn = (w: WallpapersResponse) => searchQuery.trim().length < 1 || (w.title ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    const isPublished = (w: WallpapersResponse) => { const s = (w as any).status; return (!s || s === 'published') && w.hidden !== true; };
    const filtered = (allWallpapers ?? []).filter(w => resolveMediaType(w) === 'ringtone' && isPublished(w) && matchesCatFn(w) && matchesSearchFn(w));

    // Session-stable shuffled order (see src/utils/session-shuffle.ts) — same
    // seed as the Wallpapers and Live Wallpapers sections below, so all three
    // Explore sections reshuffle together only on a full app restart.
    return sessionShuffle(filtered);
  }, [allWallpapers, activeCategory, searchQuery]);

  const gridWallpapers = useMemo(() => {
    const wallpapers = filteredWallpapers ?? [];

    if (priceFilter === 'new') {
      return [...wallpapers].sort((a, b) => {
        const bCreatedAt = b.createdAt ?? b.tarobase_created_at ?? 0;
        const aCreatedAt = a.createdAt ?? a.tarobase_created_at ?? 0;
        return bCreatedAt - aCreatedAt;
      });
    }

    // Apply sortOrder when priceFilter === 'all'
    if (priceFilter === 'all') {
      if (sortOrder === 'newest') {
        return [...wallpapers].sort((a, b) => {
          const bCreatedAt = b.createdAt ?? b.tarobase_created_at ?? 0;
          const aCreatedAt = a.createdAt ?? a.tarobase_created_at ?? 0;
          return bCreatedAt - aCreatedAt;
        });
      }
      if (sortOrder === 'oldest') {
        return [...wallpapers].sort((a, b) => {
          const aCreatedAt = a.createdAt ?? a.tarobase_created_at ?? 0;
          const bCreatedAt = b.createdAt ?? b.tarobase_created_at ?? 0;
          return aCreatedAt - bCreatedAt;
        });
      }
      if (sortOrder === 'popular') {
        return [...wallpapers].sort((a, b) => {
          const bViews = (b as WallpapersResponse & { viewCount?: number }).viewCount ?? b.tarobase_created_at ?? 0;
          const aViews = (a as WallpapersResponse & { viewCount?: number }).viewCount ?? a.tarobase_created_at ?? 0;
          return bViews - aViews;
        });
      }
      // null = neutral: session-stable shuffled order (see
      // src/utils/session-shuffle.ts) — same seed as the Live Wallpapers and
      // Ringtones sections, so all three Explore sections reshuffle together
      // only on a full app restart.
      return sessionShuffle(wallpapers);
    }

    return [...wallpapers].sort((a, b) => {
      const bCreatedAt = b.createdAt ?? b.tarobase_created_at ?? 0;
      const aCreatedAt = a.createdAt ?? a.tarobase_created_at ?? 0;
      return bCreatedAt - aCreatedAt;
    });
  }, [filteredWallpapers, priceFilter, sortOrder]);

  // ── Explore category row "All" dropdown sort — applied on top of each section's
  // existing filtered list, independent per section. 'all' leaves the current
  // default order untouched; 'new' re-sorts by createdAt descending.
  const exploreSortedGridWallpapers = useMemo(
    () => sortByFilterKey(gridWallpapers, exploreWallpaperFilterKey),
    [gridWallpapers, exploreWallpaperFilterKey]
  );

  const exploreSortedLiveWallpaperItems = useMemo(
    () => sortByFilterKey(liveWallpaperItems, exploreLiveFilterKey),
    [liveWallpaperItems, exploreLiveFilterKey]
  );

  const exploreSortedRingtoneItems = useMemo(
    () => sortByFilterKey(ringtoneItems, exploreRingtoneFilterKey),
    [ringtoneItems, exploreRingtoneFilterKey]
  );

  const favoriteWallpapers = useMemo(() => {
    const favTimestamps = new Map((userFavorites ?? []).map(f => [f.wallpaperId, f.createdAt]));
    // Favorites is a personal view — trashed items are not excluded here.
    // A user who trashes a wallpaper still keeps it in Favorites until they explicitly un-favorite it.
    return (allWallpapers ?? [])
      .filter(w => favoritedIds.has(w.id))
      .sort((a, b) => (favTimestamps.get(b.id) ?? 0) - (favTimestamps.get(a.id) ?? 0));
  }, [allWallpapers, favoritedIds, userFavorites]);

  const libraryWallpapers = useMemo(() => {
    const purchaseTimestamps = new Map((userPurchases ?? []).map(p => [p.wallpaperId, p.purchasedAt]));
    return (allWallpapers ?? [])
      .filter(w => purchasedIds.has(w.id) && !trashedIds.has(w.id))
      .sort((a, b) => (purchaseTimestamps.get(b.id) ?? 0) - (purchaseTimestamps.get(a.id) ?? 0));
  }, [allWallpapers, purchasedIds, trashedIds, userPurchases]);

  // Section-aware filters for favorites and library: 0=wallpaper, 1=live-wallpaper, 2=ringtone, 3=All (By Users)
  const sectionMediaTypes: (ResolvedMediaType | null)[] = ['wallpaper', 'live-wallpaper', 'ringtone', null];
  const matchesSection = useCallback((w: WallpapersResponse, section: number) => {
    const mediaType = sectionMediaTypes[section];
    if (mediaType === null) return true; // "All" section matches everything
    return resolveMediaType(w) === mediaType;
  }, [sectionMediaTypes]);
  const favoritesForSection = useMemo(
    () => favoriteWallpapers.filter(w => matchesSection(w, activeSection)),
    [favoriteWallpapers, activeSection, matchesSection]
  );
  const libraryForSection = useMemo(
    () => libraryWallpapers.filter(w => matchesSection(w, activeSection)),
    [libraryWallpapers, activeSection, matchesSection]
  );

  const filteredFavoritesForDisplay = favoritesForSection;
  const filteredLibraryForDisplay = libraryForSection;

  const trashedWallpapers = useMemo(() => (allWallpapers ?? []).filter(w => trashedIds.has(w.id)), [allWallpapers, trashedIds]);

  const categories = useMemo(() => {
    // The admin-managed DB list is authoritative — no merge with DB-derived old categories.
    const unique = persistentCategories.filter(c => c.toLowerCase() !== 'all');
    return ['All', ...unique];
  }, [persistentCategories]);

  // ── Admin category archive (backup zip download) candidates ────────────────
  // Wallpapers only (excludes live-wallpapers/ringtones), tombstoned ('deleted')
  // items excluded the same way the rest of the app filters them out — but
  // pending/hidden items are kept since this is a full backup of the admin's
  // own content, not a public catalog view.
  const archivableWallpapers = useMemo(
    () => (allWallpapers ?? []).filter(w => resolveMediaType(w) === 'wallpaper' && (w as any).status !== 'deleted'),
    [allWallpapers]
  );

  const liveWallpaperCategories = useMemo(() => {
    const fromData = (allWallpapers ?? [])
      .filter(w => resolveMediaType(w) === 'live-wallpaper')
      .map(w => w.category)
      .filter((c): c is string => !!c && c.trim() !== '');
    const unique = [...new Set([...persistentLiveWallpaperCategories, ...fromData])].filter(c => c.toLowerCase() !== 'all');
    return ['All', ...unique];
  }, [allWallpapers, persistentLiveWallpaperCategories]);

  const ringtoneCategories = useMemo(() => {
    const fromData = (allWallpapers ?? [])
      .filter(w => resolveMediaType(w) === 'ringtone')
      .map(w => w.category)
      .filter((c): c is string => !!c && c.trim() !== '');
    const unique = [...new Set([...persistentRingtoneCategories, ...fromData])].filter(c => c.toLowerCase() !== 'all');
    return ['All', ...unique];
  }, [allWallpapers, persistentRingtoneCategories]);

  // Ringtones published in real time — used to derive which category chips to show in SeeAllRingtonesModal
  const publishedRingtones = useMemo(() => {
    return (allWallpapers ?? []).filter(w => {
      const s = (w as any).status;
      return resolveMediaType(w) === 'ringtone' && (!s || s === 'published');
    });
  }, [allWallpapers]);

  // Only show category chips that have at least one published ringtone
  const visibleRingtoneCategories = useMemo(() => {
    const populated = RINGTONE_CATEGORIES.filter(c =>
      publishedRingtones.some(r => r.category === c)
    );
    return populated as string[];
  }, [publishedRingtones]);

  // If the selected ringtone category is no longer populated, reset to 'All'
  useEffect(() => {
    if (seeAllRingtonesCategory !== 'All' && !visibleRingtoneCategories.includes(seeAllRingtonesCategory)) {
      handleSetSeeAllRingtonesCategory('All');
    }
  }, [visibleRingtoneCategories, seeAllRingtonesCategory]);

  const currentCategories = activeSection === 0 ? categories : activeSection === 1 ? liveWallpaperCategories : activeSection === 2 ? ringtoneCategories : categories;

  // ── Category chip lists for the Explore per-section filter rows ───────────
  // Mirrors the "populated categories, sorted by freshness" logic already used
  // by the All Wallpapers / All Live / All Ringtones listing views, so Explore's
  // chips show the exact same set in the exact same order.
  const publishedWallpaperTypeItems = useMemo(
    () => (allWallpapers ?? []).filter(w => { const s = (w as any).status; return resolveMediaType(w) === 'wallpaper' && (!s || s === 'published') && w.hidden !== true; }),
    [allWallpapers]
  );
  const populatedWallpaperCategories = useMemo(() => {
    const populated = new Set<string>();
    publishedWallpaperTypeItems.forEach(w => { if (w.category) populated.add(w.category.toLowerCase()); });
    return categories.filter(cat => cat === 'All' || populated.has(cat.toLowerCase()));
  }, [publishedWallpaperTypeItems, categories]);
  const { sortedCategories: sortedExploreWallpaperCategories } = useCategoryOrder(populatedWallpaperCategories, publishedWallpaperTypeItems);

  const publishedLiveTypeItems = useMemo(
    () => (allWallpapers ?? []).filter(w => { const s = (w as any).status; return resolveMediaType(w) === 'live-wallpaper' && (!s || s === 'published') && w.hidden !== true; }),
    [allWallpapers]
  );
  const { sortedCategories: sortedExploreLiveCategories } = useCategoryOrder(liveWallpaperCategories, publishedLiveTypeItems);

  const ringtoneChipCategories = useMemo(() => ['All', ...visibleRingtoneCategories], [visibleRingtoneCategories]);
  const { sortedCategories: sortedExploreRingtoneCategories } = useCategoryOrder(ringtoneChipCategories, publishedRingtones);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    (allWallpapers ?? []).filter(w => resolveMediaType(w) === 'wallpaper').forEach(w => {
      const cat = w.category?.toLowerCase() || '';
      if (cat && cat.trim() !== '') {
        counts[cat] = (counts[cat] ?? 0) + 1;
      }
    });
    return counts;
  }, [allWallpapers]);

  // Category long-press handlers (admin only)
  const handleCategoryLongPressStart = useCallback((cat: string, e: React.TouchEvent | React.MouseEvent) => {
    if (!isAdmin || cat === 'All') return;
    const touch = 'touches' in e ? e.touches[0] : e as React.MouseEvent;
    const x = touch.clientX;
    const y = touch.clientY;
    longPressTimerRef.current = setTimeout(() => {
      setCategoryMenu({ cat, x, y });
    }, 500);
  }, [isAdmin]);

  const handleCategoryLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleCategoryContextMenu = useCallback((cat: string, e: React.MouseEvent) => {
    if (!isAdmin || cat === 'All') return;
    e.preventDefault();
    setCategoryMenu({ cat, x: e.clientX, y: e.clientY });
  }, [isAdmin]);

  const handleCategoryRename = async () => {
    if (!renameDialog || !renameValue.trim() || categoryActionLoading) return;
    const oldName = renameDialog.cat;
    const newName = renameValue.trim();
    const section: CategorySection = renameDialog.section ?? 'wallpaper';
    if (newName === oldName) { setRenameDialog(null); return; }
    setCategoryActionLoading(true);
    try {
      const toUpdate = (allWallpapers ?? []).filter(w =>
        resolveMediaType(w) === section && w.category?.toLowerCase() === oldName.toLowerCase()
      );
      const results = await Promise.all(toUpdate.map(w => updateWallpapers(w.id, { category: newName })));
      const failed = results.filter(r => !r).length;
      if (failed === 0) {
        if (section === 'wallpaper') {
          // Write renamed category list to DB
          const idx = persistentCategories.findIndex(c => c.toLowerCase() === oldName.toLowerCase());
          const next = [...persistentCategories];
          if (idx === -1) next.push(newName); else next[idx] = newName;
          await setPersistentCategories(next);
        } else if (section === 'live-wallpaper') {
          setPersistentLiveWallpaperCategories(prev => {
            const idx = prev.findIndex(c => c.toLowerCase() === oldName.toLowerCase());
            if (idx === -1) return [...prev, newName];
            const next = [...prev]; next[idx] = newName; return next;
          });
        } else {
          setPersistentRingtoneCategories(prev => {
            const idx = prev.findIndex(c => c.toLowerCase() === oldName.toLowerCase());
            if (idx === -1) return [...prev, newName];
            const next = [...prev]; next[idx] = newName; return next;
          });
        }
        if (activeCategory.toLowerCase() === oldName.toLowerCase()) setActiveCategory(newName);
      } else {
        notify.error(`${failed} item(s) failed to update`, user?.address);
      }
    } catch {
      notify.error('Failed to rename category', user?.address);
    } finally {
      setCategoryActionLoading(false);
      setRenameDialog(null);
      setRenameValue('');
    }
  };

  const handleCategoryClear = async () => {
    if (!clearConfirm || categoryActionLoading) return;
    const catName = clearConfirm.cat;
    const section: CategorySection = clearConfirm.section ?? 'wallpaper';
    setCategoryActionLoading(true);
    try {
      const toUpdate = (allWallpapers ?? []).filter(w =>
        resolveMediaType(w) === section && w.category?.toLowerCase() === catName.toLowerCase()
      );
      const results = await Promise.all(toUpdate.map(w => updateWallpapers(w.id, { category: '' })));
      const failed = results.filter(r => !r).length;
      if (failed === 0) {
        if (section === 'wallpaper') {
          // Keep the category name in the DB list so it stays visible as empty
          if (!persistentCategories.some(c => c.toLowerCase() === catName.toLowerCase())) {
            await setPersistentCategories([...persistentCategories, catName]);
          }
        } else if (section === 'live-wallpaper') {
          setPersistentLiveWallpaperCategories(prev => {
            if (prev.some(c => c.toLowerCase() === catName.toLowerCase())) return prev;
            return [...prev, catName];
          });
        } else {
          setPersistentRingtoneCategories(prev => {
            if (prev.some(c => c.toLowerCase() === catName.toLowerCase())) return prev;
            return [...prev, catName];
          });
        }
      } else {
        notify.error(`${failed} item(s) failed to update`, user?.address);
      }
    } catch {
      notify.error('Failed to clear category', user?.address);
    } finally {
      setCategoryActionLoading(false);
      setClearConfirm(null);
    }
  };

  const handleCategoryDelete = async () => {
    if (!deleteConfirm || categoryActionLoading) return;
    const catName = deleteConfirm.cat;
    const section: CategorySection = deleteConfirm.section ?? 'wallpaper';
    setCategoryActionLoading(true);
    try {
      const toUpdate = (allWallpapers ?? []).filter(w =>
        resolveMediaType(w) === section && w.category?.toLowerCase() === catName.toLowerCase()
      );
      const results = await Promise.all(toUpdate.map(w => updateWallpapers(w.id, { category: '' })));
      const failed = results.filter(r => !r).length;
      if (failed === 0) {
        if (section === 'wallpaper') {
          await setPersistentCategories(persistentCategories.filter(c => c.toLowerCase() !== catName.toLowerCase()));
        } else if (section === 'live-wallpaper') {
          setPersistentLiveWallpaperCategories(prev => prev.filter(c => c.toLowerCase() !== catName.toLowerCase()));
        } else {
          setPersistentRingtoneCategories(prev => prev.filter(c => c.toLowerCase() !== catName.toLowerCase()));
        }
        if (activeCategory.toLowerCase() === catName.toLowerCase()) setActiveCategory('All');
      } else {
        notify.error(`${failed} item(s) failed to update`, user?.address);
      }
    } catch {
      notify.error('Failed to delete category', user?.address);
    } finally {
      setCategoryActionLoading(false);
      setDeleteConfirm(null);
    }
  };

  const handlePurchase = async (wallpaper: WallpapersResponse) => {
    if (!user) { haptic.error(); notify.error('Connect your wallet to buy'); return; }
    if (purchasedIds.has(wallpaper.id)) return;
    // Compare in raw micro-SKR (integer) to avoid float rounding edge cases.
    // skrBalance is in whole SKR (divided by 1_000_000 inside useSkrBalance),
    // so multiply back to get micro-SKR for comparison.
    const priceRaw = wallpaper.price ?? 1_000_000;
    if (skrBalance !== null && Math.floor(skrBalance * 1_000_000) < priceRaw) {
      haptic.error();
      notify.error('Insufficient SKR balance', user?.address);
      return;
    }
    await executePurchase(wallpaper);
  };

  const executePurchase = async (wallpaper: WallpapersResponse) => {
    if (!user) return;
    setPurchasingId(wallpaper.id);
    // Bump the balance refresh key immediately so the hook re-fetches a fresh
    // on-chain balance. The burn itself will still fail on-chain if the balance
    // is too low — this just keeps the displayed balance in sync.
    setBalanceRefreshKey(k => k + 1);
    const PURCHASE_TIMEOUT_MS = 90_000;
    const purchaseTimeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('PURCHASE_TIMEOUT')), PURCHASE_TIMEOUT_MS)
    );

    try {
      const purchaseId = crypto.randomUUID();

      // Step 1: Create onchain purchase (triggers SKR burn).
      // Race against a 90-second timeout — if the wallet SDK hangs (e.g. after a
      // Connect popup on a new origin without proceeding to Sign), the timeout
      // rejects so the finally block can reset the purchasing state.
      const purchaseAmt = wallpaper.price ?? Number(SKR_PRICE_RAW);
      const purchaseResult = await Promise.race([
        setPurchaseAndCaptureSignature(purchaseId, {
          buyer: Address.publicKey(user.address),
          wallpaperId: wallpaper.id,
          amt: purchaseAmt,
          createdAt: Time.Now,
          creator: Address.publicKey(wallpaper.uploadedBy),
        }),
        purchaseTimeoutPromise,
      ]);

      if (!purchaseResult.ok) {
        console.error('setPurchases failed — policy denial or insufficient SKR balance');
        haptic.error();
        notify.error('Purchase failed. The wallpaper price may be invalid or your balance is too low — please try again.', user?.address);
        return;
      }

      if (!purchaseResult.txSignature) {
        // The backend now requires the on-chain transaction signature to verify payment
        // before granting library access — without it, confirm-purchase would be rejected.
        console.error('Onchain purchase succeeded but no transaction signature was returned — cannot confirm purchase');
        haptic.error();
        notify.error('Purchase could not be confirmed. Please contact support if SKR was deducted.', user?.address);
        return;
      }

      // Step 2: Call backend to record the ownership entry.
      // The onchain SKR transfer already succeeded at this point (purchaseResult.ok === true),
      // so any error here must NOT be surfaced as "purchase failed" — the tokens moved.
      // We isolate this call in its own try/catch so failures are logged silently and
      // the UI still transitions to the "owned" state.
      const mediaType = resolveMediaType(wallpaper);
      try {
        const token = await getIdToken();
        if (token) {
          const authApi = createAuthenticatedApiClient(token, user.address);
          await authApi.post('/api/confirm-purchase', {
            wallpaperId: wallpaper.id,
            txSignature: purchaseResult.txSignature,
          });
        } else {
          console.warn('confirm-purchase skipped: could not obtain auth token after successful onchain transfer');
        }
      } catch (confirmError: unknown) {
        // The onchain transfer already happened — ownership will sync once the
        // subscription propagates or on the next page load. Log but don't surface as failure.
        console.error('confirm-purchase backend call failed after successful onchain transfer:', confirmError);
      }

      // Refresh balance and update UI regardless of backend record outcome.
      setBalanceRefreshKey(k => k + 1);
      // Optimistic update for the ringtone full-screen player: flip isPurchased
      // immediately so the Buy button changes to Owned and the burst fires without
      // waiting for the subscription to refresh.
      if (mediaType === 'ringtone' && ringtonePlayer.currentRingtone?.id === wallpaper.id) {
        ringtonePlayer.setPurchased(true);
      }
    } catch (error: unknown) {
      console.error('Purchase error:', error);
      // Handle wallet-connect timeout — SDK hangs after Connect popup on a new origin
      if (error instanceof Error && error.message === 'PURCHASE_TIMEOUT') {
        haptic.error();
        notify.error('Purchase timed out. Make sure your wallet is connected to this site, then try again.', user?.address);
        return;
      }
      // Detect voluntary user rejection — don't show an error toast in that case
      const isUserRejection = (() => {
        if (!error || typeof error !== 'object') return false;
        const e = error as Record<string, unknown>;
        if (e.code === 4001) return true;
        if (e.name === 'UserRejectedRequestError') return true;
        const msg = typeof e.message === 'string' ? e.message.toLowerCase() : '';
        return (
          msg.includes('user rejected') ||
          msg.includes('rejected the request') ||
          msg.includes('user declined') ||
          msg.includes('cancelled') ||
          msg.includes('canceled')
        );
      })();
      if (isUserRejection) {
        return;
      }
      haptic.error();
      notify.error('Purchase failed. Please try again.', user?.address);
    } finally {
      setPurchasingId(null);
    }
  };

  const handleToggleFavorite = useCallback(async (wallpaper: WallpapersResponse) => {
    if (!user) { notify.error('Connect your wallet to save favorites'); return; }
    const isCurrentlyFavorited = favoritedIds.has(wallpaper.id);
    if (isCurrentlyFavorited) {
      // Optimistically remove immediately
      setOptimisticFavRemoved(prev => new Set([...prev, wallpaper.id]));
      setOptimisticFavAdded(prev => { const next = new Set(prev); next.delete(wallpaper.id); return next; });
      const ok = await deleteUsersFavorites(user.address, wallpaper.id);
      if (!ok) {
        // Revert on failure
        setOptimisticFavRemoved(prev => { const next = new Set(prev); next.delete(wallpaper.id); return next; });
      }
    } else {
      // Optimistically add immediately
      setOptimisticFavAdded(prev => new Set([...prev, wallpaper.id]));
      setOptimisticFavRemoved(prev => { const next = new Set(prev); next.delete(wallpaper.id); return next; });
      const ok = await setUsersFavorites(user.address, wallpaper.id, {
        wallpaperId: wallpaper.id,
        owner: Address.publicKey(user.address),
        createdAt: Time.Now,
      });
      if (!ok) {
        // Revert on failure
        setOptimisticFavAdded(prev => { const next = new Set(prev); next.delete(wallpaper.id); return next; });
      }
    }
  }, [user, favoritedIds]);

  const handleTrash = async (wallpaper: WallpapersResponse) => {
    if (!user) { haptic.error(); notify.error('Connect your wallet to use trash'); return; }
    const success = await setUsersTrash(user.address, wallpaper.id, {
      wallpaperId: wallpaper.id,
      owner: Address.publicKey(user.address),
      deletedAt: Time.Now,
    });
    if (success) {
      haptic.medium();
      viewerReturnToSeeAllRef.current = false;
      setShuffledViewerList(null);
      setSelectedWallpaper(null);
    } else {
      haptic.error();
      notify.error('Failed to move to trash', user?.address);
    }
  };

  const handleRestore = async (wallpaperId: string) => {
    if (!user) return;
    const success = await deleteUsersTrash(user.address, wallpaperId);
    if (success) {
      haptic.light();
    } else {
      haptic.error();
      notify.error('Failed to restore', user?.address);
    }
  };

  const handleDeletePermanently = async (wallpaperId: string) => {
    if (!user) return;
    // Look up the purchase document by its wallpaperId field to get the actual UUID doc ID,
    // then delete by that UUID. Documents use auto-generated UUIDs, not the wallpaper slug.
    const purchases = await getManyUsersPurchases(user.address, `where wallpaperId = '${wallpaperId}'`);
    let purchaseDeleted = true;
    if (purchases && purchases.length > 0) {
      for (const purchase of purchases) {
        const ok = await deleteUsersPurchases(user.address, purchase.id);
        if (!ok) purchaseDeleted = false;
      }
    }
    // Free wallpapers may have no purchase record — that is fine, treat as success.
    const trashDeleted = await deleteUsersTrash(user.address, wallpaperId);
    if (!trashDeleted || !purchaseDeleted) {
      haptic.error();
      notify.error('Failed to delete', user?.address);
    } else {
      haptic.success();
    }
  };

  const handleSelectWallpaper = useCallback((w: WallpapersResponse, fromLibrary: boolean) => {
    // Ringtones always open the RingtoneFullScreenPlayer, never WallpaperViewer.
    // Use the contextual mixed playlist so prev/next can cross into visual items
    // when opened from the Favorites or Library page.
    if (resolveMediaType(w) === 'ringtone') {
      if (fromLibrary) {
        setViewerSource('library');
        ringtonePlayer.play(w, [...libraryWallpapers]);
      } else if (activeTab === 'favorites') {
        setViewerSource('favorites');
        ringtonePlayer.play(w, [...favoriteWallpapers]);
      } else {
        ringtonePlayer.play(w, [...ringtoneItems]);
      }
      ringtonePlayer.openFullScreen();
      return;
    }

    let sourceList: WallpapersResponse[];
    if (fromLibrary) {
      setViewerSource('library');
      viewerFilteredListRef.current = null;
      sourceList = libraryWallpapers;
    } else if (activeTab === 'favorites') {
      setViewerSource('favorites');
      viewerFilteredListRef.current = null;
      sourceList = favoriteWallpapers;
    } else if (resolveMediaType(w) === 'live-wallpaper') {
      setViewerSource('live');
      viewerFilteredListRef.current = null;
      sourceList = liveWallpaperItems;
    } else {
      setViewerSource('grid');
      viewerFilteredListRef.current = null;
      sourceList = gridWallpapers;
    }
    lastViewedWallpaperIdRef.current = w.id;
    window.history.pushState({ page: 'sub' }, '');
    setShuffledViewerList([...sourceList]);
    setSelectedWallpaper(w);
  }, [activeTab, ringtonePlayer, ringtoneItems, libraryWallpapers, favoriteWallpapers, liveWallpaperItems, gridWallpapers]);

  const settingsPanelSlot = (
    <div style={{
      borderRadius: 14,
      border: tokens.border,
      background: tokens.bgCard,
      overflow: 'hidden',
    }}>
      {/* Grid Columns selector */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 8,
        padding: '13px 15px',
        background: tokens.bgSettingsInner,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: tokens.textPrimary, fontFamily: "'Syne', sans-serif" }}>
            Grid Columns
          </span>
          <span style={{ fontSize: 11, color: tokens.textMuted, fontFamily: "'Syne', sans-serif" }}>
            Number of columns in gallery
          </span>
        </div>
        <div style={{
          display: 'flex', gap: 4,
          background: tokens.bgInput,
          borderRadius: 10, padding: 3,
          border: tokens.border,
        }}>
          {GRID_COLUMN_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleGridColumnsChange(opt.value)}
              style={{
                flex: 1, padding: '6px 4px',
                borderRadius: 7, border: 'none',
                background: gridColumns === opt.value ? 'rgba(139,92,246,0.85)' : 'transparent',
                color: gridColumns === opt.value ? '#fff' : tokens.textVeryMuted,
                fontSize: 12,
                fontWeight: gridColumns === opt.value ? 700 : 500,
                cursor: 'pointer',
                fontFamily: "'Syne', sans-serif",
                transition: 'all 0.18s ease',
                boxShadow: gridColumns === opt.value ? '0 1px 6px rgba(139,92,246,0.4)' : 'none',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );


  // Arrow-driven section switcher rendered in the Explore header in place of
  // the plain "SeekScape" / "Preview" wordmark. Locked (arrows inert, label
  // forced to "Preview") while logged out so the restricted-preview signal
  // that used to live in the wordmark is preserved.
  const homeTitleSlot = (
    <SectionArrowSwitcher
      activeIndex={activeSection}
      onIndexChange={handleSectionIndexChange}
      locked={!user}
      labelOverride={!user ? 'Preview' : undefined}
    />
  );

  // ── Library header trailing action — icon-only bulk-trash button, shown
  // only while multi-select is active with at least one item selected.
  // Compact tap target, no background/border block. Kept mounted (as an
  // AnimatePresence with a conditional child) only while on the Library tab
  // so enter/exit can mirror each other; entirely absent on every other tab.
  const libraryHeaderTrailingSlot = activeTab === 'library' ? (
    <AnimatePresence mode="wait" initial={false}>
      {librarySelectionMode && librarySelectedIds.size > 0 && (
        <motion.button
          key="library-bulk-trash"
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1, transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] } }}
          exit={{ opacity: 0, scale: 0.7, transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] } }}
          whileTap={{ scale: 0.86 }}
          onClick={() => { haptic.medium(); handleLibraryBulkTrash(); }}
          aria-label="Move selected to trash"
          style={{
            width: 34,
            height: 34,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: '#fca5a5',
            flexShrink: 0,
          }}
        >
          <Trash2 size={19} strokeWidth={2} />
        </motion.button>
      )}
    </AnimatePresence>
  ) : undefined;

  // ── Admin tools — moved out of the profile body into the Profile header row.
  // Icon-only (no labels), same order and colors as the former Admin Tools grid:
  // Add / Txns / Categories / Moderation / Archive. Bare icons pinned to the
  // header's right edge, matching the Library bulk-trash trailing action.
  const adminHeaderTrailingSlot = isAdmin && activeTab === 'menu' ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {[
        { key: 'add', Icon: Plus, color: '#a78bfa', strokeWidth: 2, label: 'Add item', onClick: () => setShowAddWallpaper(true) },
        { key: 'txns', Icon: Receipt, color: '#a855f7', strokeWidth: 1.8, label: 'Transactions', onClick: () => setShowTransactions(true) },
        { key: 'categories', Icon: FolderCog, color: '#f59e0b', strokeWidth: 1.8, label: 'Manage categories', onClick: () => setShowManageCategories(true) },
        { key: 'moderation', Icon: ShieldCheck, color: '#eab308', strokeWidth: 1.8, label: 'Moderation', onClick: () => setShowModeration(true) },
        { key: 'archive', Icon: Archive, color: '#22d3ee', strokeWidth: 1.8, label: 'Archive wallpaper category', onClick: () => setShowArchiveCategory(true) },
      ].map(({ key, Icon, color, strokeWidth, label, onClick }) => (
        <motion.button
          key={key}
          whileTap={{ scale: 0.86 }}
          onClick={() => { haptic.light(); onClick(); }}
          aria-label={label}
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <Icon size={19} color={color} strokeWidth={strokeWidth} />
        </motion.button>
      ))}
    </div>
  ) : undefined;

  // ── Search control — relocated out of the header into the category chip
  // row rendered directly below it. Rendered as the first item in that row
  // (before "All") for every Explore section via the shared CollapsibleSearch
  // component, so Wallpapers / Live / Ringtones / By Users all get identical
  // behavior. Collapsed = just a magnifier icon matching the inactive chip
  // pill styling; tapping it slides the input open. Hidden entirely when
  // logged out, matching the header search icon's prior auth gating. Typing
  // here drives the same searchQuery state as before, so per-section
  // filtering and the cross-section results overlay work exactly as they did
  // prior to the move.
  const searchChipSlot = user ? (
    <CollapsibleSearch
      value={searchQuery}
      onChange={setSearchQuery}
      placeholder="Find your vibe..."
      open={searchOpen}
      onOpenChange={setSearchOpen}
      inputRef={searchChipInputRef}
    />
  ) : null;

  // ── Search results overlay content (shown in the header search overlay) ──
  // Do NOT close the search overlay here — leave it mounted underneath the
  // viewer (which renders at z-index 1000+, well above the search panel).
  // Closing search first would trigger useBackButtonClose cleanup which fires
  // an async history.back() that races with the viewer's pushState and
  // instantly destroys the viewer. Instead, track that the viewer was opened
  // from search so that when the viewer closes we can correctly restore the
  // search overlay rather than clearing it.
  const handleSelectFromSearch = useCallback((w: WallpapersResponse) => {
    viewerOpenedFromSearchRef.current = true;
    setViewerOpenedFromSearch(true);
    handleSelectWallpaper(w, false);
  }, [handleSelectWallpaper]);

  // ── Creator search results: filter userProfiles by username containing query ──
  const matchingCreators = useMemo(() => {
    if (searchQuery.trim().length < 1) return [];
    const q = searchQuery.toLowerCase();
    return (allUserProfiles ?? []).filter(
      (p) => p.username && p.username.toLowerCase().includes(q),
    );
  }, [allUserProfiles, searchQuery]);

  // Build a map of walletAddress → published upload count from allWallpapers
  const uploadCountByWallet = useMemo(() => {
    const map: Record<string, number> = {};
    for (const w of allWallpapers ?? []) {
      const s = (w as WallpapersResponse & { status?: string }).status;
      if (s && s !== 'published') continue;
      if (!w.uploadedBy) continue;
      map[w.uploadedBy] = (map[w.uploadedBy] ?? 0) + 1;
    }
    return map;
  }, [allWallpapers]);

  const searchResultsSlot = searchQuery.trim().length >= 1 ? (
    <div style={{ paddingTop: 8 }}>
      {/* ── CREATORS section ── */}
      {matchingCreators.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <p style={{
            padding: '8px 14px 6px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: tokens.textVeryMuted,
            margin: 0,
            fontFamily: "'Syne', sans-serif",
          }}>
            Creators · {matchingCreators.length}
          </p>
          {matchingCreators.map((creator) => {
            const uploadCount = uploadCountByWallet[creator.id] ?? 0;
            return (
              <button
                key={creator.id}
                onClick={() => {
                  // Signal that the upcoming navigation is intentional so
                  // CategoryPageShell's useBackButtonClose cleanup skips
                  // history.back() on unmount (which would undo the navigation).
                  markSearchNavigatingRef.current?.();
                  navigate(`/user/${creator.username}`);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <CreatorAvatar walletAddress={creator.id} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0,
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'rgba(167,139,250,0.85)',
                    fontFamily: "'DM Mono', monospace",
                    letterSpacing: '0.01em',
                    fontStyle: 'normal',
                  }}>
                    @{creator.username}
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: 12,
                    color: tokens.textMuted,
                    fontFamily: "'DM Mono', monospace",
                    marginTop: 1,
                  }}>
                    {uploadCount} upload{uploadCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {/* Section label: Wallpapers */}
      {gridWallpapers.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <p style={{
            padding: '8px 14px 6px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: tokens.textVeryMuted,
            margin: 0,
            fontFamily: "'Syne', sans-serif",
          }}>
            Wallpapers · {gridWallpapers.length}
          </p>
          <GalleryGrid
            wallpapers={gridWallpapers}
            emptyMessage=""
            favoritedIds={favoritedIds}
            purchasedIds={purchasedIds}
            onToggleFavorite={handleToggleFavorite}
            onSelectWallpaper={handleSelectFromSearch}
            gridColumns={gridColumns}
            minimal
            disableVirtualization
          />
        </div>
      )}
      {/* Section label: Live Wallpapers */}
      {liveWallpaperItems.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <p style={{
            padding: '8px 14px 6px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: tokens.textVeryMuted,
            margin: 0,
            fontFamily: "'Syne', sans-serif",
          }}>
            Live Wallpapers · {liveWallpaperItems.length}
          </p>
          <GalleryGrid
            wallpapers={liveWallpaperItems}
            emptyMessage=""
            favoritedIds={favoritedIds}
            purchasedIds={purchasedIds}
            onToggleFavorite={handleToggleFavorite}
            onSelectWallpaper={handleSelectFromSearch}
            gridColumns={gridColumns}
            minimal
            disableVirtualization
          />
        </div>
      )}
      {/* Section label: Ringtones */}
      {ringtoneItems.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <p style={{
            padding: '8px 14px 6px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: tokens.textVeryMuted,
            margin: 0,
            fontFamily: "'Syne', sans-serif",
          }}>
            Ringtones · {ringtoneItems.length}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 10px 8px' }}>
            {ringtoneItems.map((w) => (
              <RingtoneRow
                key={w.id}
                wallpaper={w}
                isFavorited={favoritedIds.has(w.id)}
                isPurchased={purchasedIds.has(w.id)}
                playlist={ringtoneItems}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>
        </div>
      )}
      {/* No results at all */}
      {matchingCreators.length === 0 && gridWallpapers.length === 0 && liveWallpaperItems.length === 0 && ringtoneItems.length === 0 && (
        <NoSearchResults
          query={searchQuery}
          categories={categories}
          activeCategory={activeCategory}
          onClear={() => { setSearchQuery(''); setSearchOpen(false); }}
          onTryAnother={() => {
            setSearchQuery('');
            focusSearchInputRef.current?.();
          }}
          onPickCategory={(cat) => {
            setSearchQuery('');
            setSearchOpen(false);
            setActiveCategory(cat);
            setActiveTab('home');
          }}
        />
      )}
    </div>
  ) : undefined;

  return (
    <CategoryPageShell
      activeTab={activeTab}
      onTabChange={(tab) => {
        // Record the current tab so the back button can return to it.
        prevTabRef.current = activeTab;
        setShowSeeAllModal(false);
        setShowSeeAllRingtonesModal(false);
        setShowSeeAllLiveModal(false);
        setSelectedWallpaper(null);
        setShuffledViewerList(null);
        setLibrarySelectionMode(false);
        setLibrarySelectedIds(new Set());
        setActiveTab(tab);
      }}
      user={user}
      onLogout={logout}
      accentColor={currentAccentColor}
      title={activeTab === 'menu' ? 'Profile' : activeTab === 'library' ? 'Library' : activeTab === 'favorites' ? 'Favorites' : activeTab === 'sales' ? 'Sales' : user ? 'SeekScape' : 'Preview'}
      titleSlot={activeTab === 'home' ? homeTitleSlot : undefined}
      headerTrailingSlot={libraryHeaderTrailingSlot ?? adminHeaderTrailingSlot}
      searchOpen={searchOpen}
      onSearchOpenChange={handleSearchOpenChange}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchResultsSlot={searchResultsSlot}
      onSearchMarkNavigatingReady={(fn) => { markSearchNavigatingRef.current = fn; }}
      suppressSearchBackClose={viewerOpenedFromSearch}
      settingsPanelSlot={settingsPanelSlot}
      settingsPanelWidth={280}
      hideBottomNav={selectedWallpaper !== null || showTrash || showAddWallpaper || showTransactions || showArchiveCategory || showManageCategories || moderationFullViewOpen || showMyUploads || showModeration || showSubscriptionsDialog}
      navScrollVisible={!showSeeAllModal && !showSeeAllRingtonesModal && !showSeeAllLiveModal ? true : modalNavVisible}
      unreadNotificationCount={unreadNotificationCount}
    >
      {/* ── Shared: Favorites tab ──────────────────────────────────────── */}
      <AnimatePresence>
        {activeTab === 'favorites' && (
          <motion.div key="favorites" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
            {!user ? (
              <AuthGate
                title="Connect your wallet to view your favorites"
                subtitle="Save and access your favorite items in one place"
                onConnect={handleLogin}
              />
            ) : (
              <FavoritesView
                favorites={favoriteWallpapers}
                favoritedIds={favoritedIds}
                purchasedIds={purchasedIds}
                onToggleFavorite={handleToggleFavorite}
                onSelectWallpaper={(w) => { setViewerSource('favorites'); handleSelectWallpaper(w, false); }}
                onBrowse={() => setActiveTab('home')}
              />
            )}
          </motion.div>
        )}

        {/* ── Shared: Library tab ─────────────────────────────────────── */}
        {activeTab === 'library' && (
          <motion.div key="library" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
            {loading ? (
              <div style={{ minHeight: '100vh', background: tokens.bg }} />
            ) : !user ? (
              <AuthGate
                title="Connect your wallet to access your library"
                subtitle="Sign in to view your purchased wallpapers and favorites"
                onConnect={handleLogin}
              />
            ) : (
              <LibraryView
                libraryItems={libraryWallpapers}
                isLoggedIn={!!user}
                onSelectWallpaper={handleSelectWallpaper}
                favoritedIds={favoritedIds}
                onToggleFavorite={handleToggleFavorite}
                onBrowse={() => setActiveTab('home')}
                selectionMode={librarySelectionMode}
                selectedIds={librarySelectedIds}
                onLongPressItem={handleLibraryLongPressItem}
                onToggleSelectItem={handleLibraryToggleSelectItem}
              />
            )}
          </motion.div>
        )}

        {/* ── Shared: Menu tab ────────────────────────────────────────── */}
        {activeTab === 'menu' && (
          <motion.div key="menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
            {loading ? (
              <div style={{ minHeight: '100vh', background: tokens.bg }} />
            ) : !user ? (
              <AuthGate
                title="Connect your wallet to open your profile"
                subtitle="Sign in to view your profile and account"
                onConnect={handleLogin}
              />
            ) : (
              <MenuPanel
                user={user}
                skrBalance={skrBalance}
                purchaseCount={libraryWallpapers.length}
                favoriteCount={favoriteWallpapers.length}
                trashCount={trashedIds.size}
                onOpenTrash={() => { setShowTrash(true); }}
                onOpenMyUploads={handleOpenMyUploads}
                onOpenSales={() => { prevTabRef.current = 'menu'; setActiveTab('sales'); window.history.pushState({ page: 'sub' }, ''); }}
                onOpenSubscriptions={() => { setShowSubscriptionsDialog(true); window.history.pushState({ page: 'sub' }, ''); }}
                subscriptionCount={subscriptionCount}
                onNavigateSettings={() => { navigate('/settings'); }}
                onLogout={logout}
                accentColor="#a78bfa"
                existingCategories={categories?.filter(c => c !== 'All')}
                userProfileUsername={userProfileUsername}
                userProfileComplete={userProfileComplete}
                myUploadCount={myUploadCount}
                salesSkrTotal={salesSkrTotal}
                exploreMode={exploreMode}
                onToggleExploreMode={handleToggleExploreMode}
              />
            )}
          </motion.div>
        )}

        {/* ── Sales tab ───────────────────────────────────────────────────── */}
        {activeTab === 'sales' && (
          <motion.div key="sales" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
            {loading ? (
              <div style={{ minHeight: '100vh', background: tokens.bg }} />
            ) : !user ? (
              <AuthGate
                title="Connect your wallet to view your sales"
                subtitle="Track your earnings and sales history"
                onConnect={handleLogin}
              />
            ) : (
              <div style={{ padding: '16px 10px 120px' }}>
                <div style={{
                  background: 'hsl(246, 38%, 8%)',
                  border: '1px solid hsl(246, 24%, 14%)',
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                }}>
                  <TransactionsList
                    creatorAddress={user.address}
                    showExternalLinks={true}
                    enabled={activeTab === 'sales'}
                  />
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ── Swipeable home content (only rendered when activeTab === 'home') ── */}
        {activeTab === 'home' && (
          <motion.div key="home-sections" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
            {/* Outer clip container — native touch listeners attached via useEffect.
                minHeight fills the viewport down to the floating nav so short sections
                (e.g. a single card) keep the empty space below swipeable. */}
            <div
              ref={swipeCallbackRef as React.RefCallback<HTMLDivElement>}
              style={{
                overflow: 'hidden',
                touchAction: 'pan-y',
                minHeight: 'calc(100svh - 66px - env(safe-area-inset-top, 0px) - 100px - env(safe-area-inset-bottom, 0px))',
              }}
            >
              {/* Inner horizontal track — slides via CSS transition */}
              {/* Position: relative so absolutely-positioned inactive columns are
                  contained within the track. The active column stays in normal
                  flow so the track's height matches the active section naturally,
                  with no JS measurement needed. */}
              <div
                style={{
                  display: 'flex',
                  width: `${SECTIONS.length * 100}%`,
                  // Rank 1 (the pivot) is always the resting-center slot;
                  // `contentShift` (±1, only non-zero mid-slide) reveals the
                  // forward (+1) or backward (-1) neighbor already sitting
                  // one order-slot away — see `contentOrder` above.
                  transform: `translateX(-${((1 + contentShift) / SECTIONS.length) * 100}%)`,
                  transition: contentTransitionEnabled ? 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                  willChange: 'transform',
                  alignItems: 'flex-start',
                  position: 'relative',
                }}
              >
              {/* ── SECTION 0: Wallpapers ───────────────────────────────── */}
              <div ref={(el) => { sectionColRefs.current[0] = el; }} style={{ width: `${100 / SECTIONS.length}%`, flexShrink: 0, minWidth: 0, order: contentOrder(0), ...(activeSection !== 0 && contentPivot !== 0 ? { height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' } : {}) }}>
                <CategoryChipsRow
                  categories={sortedExploreWallpaperCategories}
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory}
                  showCategories={searchQuery.trim().length < 1}
                  leadingSlot={searchChipSlot}
                  filterKey={exploreWallpaperFilterKey}
                  onFilterChange={setExploreWallpaperFilterKey}
                />
                {searchQuery.trim().length >= 1 && exploreSortedGridWallpapers.length === 0 ? (
                  <div style={{ paddingTop: 16 }}>
                    <p style={{ padding: '0 12px 8px', fontSize: 12, color: tokens.textMuted, margin: 0 }}>
                      0 results for "{searchQuery}"
                    </p>
                    <NoSearchResults
                      query={searchQuery}
                      categories={categories}
                      activeCategory={activeCategory}
                      onClear={() => setSearchQuery('')}
                      onTryAnother={() => {
                        setSearchQuery('');
                        focusSearchInputRef.current?.();
                      }}
                      onPickCategory={(cat) => {
                        setSearchQuery('');
                        setActiveCategory(cat);
                      }}
                    />
                  </div>
                ) : exploreSortedGridWallpapers.length > 0 ? (
                  <div style={{ paddingTop: searchQuery.trim().length >= 1 ? 16 : 0 }}>
                    {searchQuery.trim().length >= 1 && (
                      <p style={{ padding: '0 12px 8px', fontSize: 12, color: tokens.textMuted, margin: 0 }}>
                        {exploreSortedGridWallpapers.length} result{exploreSortedGridWallpapers.length !== 1 ? 's' : ''} for "{searchQuery}"
                      </p>
                    )}
                    <WallpaperExploreGrid
                      wallpapers={exploreSortedGridWallpapers}
                      emptyMessage="No wallpapers yet."
                      purchasedIds={purchasedIds}
                      onSelectWallpaper={handleSelectWallpaper}
                      gridColumns={gridColumns}
                    />
                  </div>
                ) : (
                <div style={{
                  minHeight: 'calc(100vh - 160px)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '32px 24px 120px',
                  position: 'relative', overflow: 'hidden',
                }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, textAlign: 'center' }}
                  >
                    <motion.div
                      animate={{ scale: [1, 1.07, 1], rotate: [0, 3, -3, 0] }}
                      transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                      style={{
                        width: 80, height: 80, borderRadius: '50%',
                        background: 'rgba(167,139,250,0.1)',
                        border: '1.5px solid rgba(167,139,250,0.3)',
                        boxShadow: '0 0 30px rgba(167,139,250,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <ImageIcon size={32} color="#a78bfa" strokeWidth={1.6} />
                    </motion.div>
                    <div>
                      <p style={{ margin: 0, fontSize: 26, fontWeight: 800, color: tokens.textPrimary, fontFamily: "'Syne', sans-serif", letterSpacing: '-0.02em' }}>
                        Wallpapers
                      </p>
                      <p style={{ margin: '8px 0 0', fontSize: 14, color: tokens.textMuted, fontFamily: "'Syne', sans-serif", maxWidth: 260, lineHeight: 1.55 }}>
                        No wallpapers in this category yet.
                      </p>
                    </div>
                  </motion.div>
                </div>
                )}
              </div>
              {/* ── End SECTION 0 ────────────────────────────────────────── */}

              {/* ── SECTION 1: Live Wallpapers ──────────────────────────── */}
              <div ref={(el) => { sectionColRefs.current[1] = el; }} style={{ width: `${100 / SECTIONS.length}%`, flexShrink: 0, minWidth: 0, order: contentOrder(1), ...(activeSection !== 1 && contentPivot !== 1 ? { height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' } : {}) }}>
                <CategoryChipsRow
                  categories={sortedExploreLiveCategories}
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory}
                  showCategories={searchQuery.trim().length < 1}
                  leadingSlot={searchChipSlot}
                  filterKey={exploreLiveFilterKey}
                  onFilterChange={setExploreLiveFilterKey}
                />
                {searchQuery.trim().length >= 1 && exploreSortedLiveWallpaperItems.length === 0 ? (
                  <div style={{ paddingTop: 16 }}>
                    <p style={{ padding: '0 12px 8px', fontSize: 12, color: tokens.textMuted, margin: 0 }}>
                      0 results for "{searchQuery}"
                    </p>
                    <NoSearchResults
                      query={searchQuery}
                      categories={liveWallpaperCategories}
                      activeCategory={activeCategory}
                      onClear={() => setSearchQuery('')}
                      onTryAnother={() => {
                        setSearchQuery('');
                        focusSearchInputRef.current?.();
                      }}
                      onPickCategory={(cat) => {
                        setSearchQuery('');
                        setActiveCategory(cat);
                      }}
                    />
                  </div>
                ) : exploreSortedLiveWallpaperItems.length > 0 ? (
                  <div style={{ paddingTop: searchQuery.trim().length >= 1 ? 16 : 0 }}>
                    {searchQuery.trim().length >= 1 && (
                      <p style={{ padding: '0 12px 8px', fontSize: 12, color: tokens.textMuted, margin: 0 }}>
                        {exploreSortedLiveWallpaperItems.length} result{exploreSortedLiveWallpaperItems.length !== 1 ? 's' : ''} for "{searchQuery}"
                      </p>
                    )}
                    <GalleryGrid
                      wallpapers={exploreSortedLiveWallpaperItems}
                      emptyMessage="No live wallpapers yet."
                      favoritedIds={favoritedIds}
                      purchasedIds={purchasedIds}
                      onToggleFavorite={handleToggleFavorite}
                      onSelectWallpaper={handleSelectWallpaper}
                      gridColumns={gridColumns}
                      minimal
                    />
                  </div>
                ) : (
                <div style={{
                  minHeight: 'calc(100vh - 160px)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '32px 24px 120px',
                  position: 'relative', overflow: 'hidden',
                }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, textAlign: 'center' }}
                  >
                    <motion.div
                      animate={{ scale: [1, 1.07, 1], rotate: [0, 3, -3, 0] }}
                      transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                      style={{
                        width: 80, height: 80, borderRadius: '50%',
                        background: 'rgba(167,139,250,0.1)',
                        border: '1.5px solid rgba(167,139,250,0.3)',
                        boxShadow: '0 0 30px rgba(167,139,250,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <Film size={32} color="#a78bfa" strokeWidth={1.6} />
                    </motion.div>
                    <div>
                      <p style={{ margin: 0, fontSize: 26, fontWeight: 800, color: tokens.textPrimary, fontFamily: "'Syne', sans-serif", letterSpacing: '-0.02em' }}>
                        Live Wallpapers
                      </p>
                      <p style={{ margin: '8px 0 0', fontSize: 14, color: tokens.textMuted, fontFamily: "'Syne', sans-serif", maxWidth: 260, lineHeight: 1.55 }}>
                        Animated wallpapers that bring your screen to life. Coming soon.
                      </p>
                    </div>
                    <motion.div
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      style={{
                        padding: '6px 18px', borderRadius: 999,
                        border: '1px solid rgba(167,139,250,0.3)',
                        background: 'rgba(167,139,250,0.08)',
                        fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
                        textTransform: 'uppercase' as const, color: '#a78bfa',
                        fontFamily: "'Syne', sans-serif",
                      }}
                    >
                      Coming Soon
                    </motion.div>
                  </motion.div>
                </div>
                )}
              </div>
              {/* ── End SECTION 1 ────────────────────────────────────────── */}

              {/* ── SECTION 2: Ringtones ────────────────────────────────── */}
              <div ref={(el) => { sectionColRefs.current[2] = el; }} style={{ width: `${100 / SECTIONS.length}%`, flexShrink: 0, minWidth: 0, order: contentOrder(2), ...(activeSection !== 2 && contentPivot !== 2 ? { height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' } : {}) }}>
                <CategoryChipsRow
                  categories={sortedExploreRingtoneCategories}
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory}
                  showCategories={searchQuery.trim().length < 1}
                  leadingSlot={searchChipSlot}
                  filterKey={exploreRingtoneFilterKey}
                  onFilterChange={setExploreRingtoneFilterKey}
                />
                {searchQuery.trim().length >= 1 && exploreSortedRingtoneItems.length === 0 ? (
                  <div style={{ paddingTop: 16 }}>
                    <p style={{ padding: '0 12px 8px', fontSize: 12, color: tokens.textMuted, margin: 0 }}>
                      0 results for "{searchQuery}"
                    </p>
                    <NoSearchResults
                      query={searchQuery}
                      categories={ringtoneCategories}
                      activeCategory={activeCategory}
                      onClear={() => setSearchQuery('')}
                      onTryAnother={() => {
                        setSearchQuery('');
                        focusSearchInputRef.current?.();
                      }}
                      onPickCategory={(cat) => {
                        setSearchQuery('');
                        setActiveCategory(cat);
                      }}
                    />
                  </div>
                ) : exploreSortedRingtoneItems.length > 0 ? (
                  <div style={{ paddingTop: searchQuery.trim().length >= 1 ? 16 : 4 }}>
                    {searchQuery.trim().length >= 1 && (
                      <p style={{ padding: '0 12px 8px', fontSize: 12, color: tokens.textMuted, margin: 0 }}>
                        {exploreSortedRingtoneItems.length} result{exploreSortedRingtoneItems.length !== 1 ? 's' : ''} for "{searchQuery}"
                      </p>
                    )}
                    {/* Ringtone rows — full catalogue, matches "All Ringtones" */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 7, paddingRight: 7 }}>
                      {exploreSortedRingtoneItems.map((item) => (
                        <AdminEditableCard key={item.id} item={item}>
                          <RingtoneRow
                            wallpaper={item}
                            isFavorited={favoritedIds.has(item.id)}
                            isPurchased={purchasedIds.has(item.id)}
                            playlist={exploreSortedRingtoneItems}
                            onToggleFavorite={handleToggleFavorite}
                          />
                        </AdminEditableCard>
                      ))}
                    </div>
                  </div>
                ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '80px 24px 140px', gap: 16, textAlign: 'center',
                }}>
                  <motion.div
                    animate={{ scale: [1, 1.07, 1] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                      width: 80, height: 80, borderRadius: '50%',
                      background: 'rgba(167,139,250,0.1)',
                      border: '1.5px solid rgba(167,139,250,0.3)',
                      boxShadow: '0 0 30px rgba(167,139,250,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Bell size={32} color="#a78bfa" strokeWidth={1.6} />
                  </motion.div>
                  <div>
                    <p style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#f0f0f5', fontFamily: "'Syne', sans-serif", letterSpacing: '-0.02em' }}>
                      Ringtones
                    </p>
                    <p style={{ margin: '8px 0 0', fontSize: 14, color: 'rgba(240,240,245,0.38)', fontFamily: "'Syne', sans-serif", maxWidth: 260, lineHeight: 1.55 }}>
                      Premium audio ringtones crafted to match your aesthetic. Coming soon.
                    </p>
                  </div>
                  <motion.div
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    style={{
                      padding: '6px 18px', borderRadius: 999,
                      border: '1px solid rgba(167,139,250,0.3)',
                      background: 'rgba(167,139,250,0.08)',
                      fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase' as const, color: '#a78bfa',
                      fontFamily: "'Syne', sans-serif",
                    }}
                  >
                    Coming Soon
                  </motion.div>
                </div>
                )}
              </div>
              {/* ── End SECTION 2 ────────────────────────────────────────── */}

              {/* ── SECTION 3: By Users (Creator Profiles) ──────────────── */}
              <div
                ref={(el) => { sectionColRefs.current[3] = el; }}
                style={{
                  width: `${100 / SECTIONS.length}%`,
                  flexShrink: 0,
                  minWidth: 0,
                  order: contentOrder(3),
                  ...(activeSection !== 3 && contentPivot !== 3 ? { height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' } : {}),
                }}
              >
                <CategoryChipsRow
                  categories={[]}
                  activeCategory=""
                  onCategoryChange={() => {}}
                  leadingSlot={searchChipSlot}
                  filterKey={exploreUsersFilterKey}
                  onFilterChange={setExploreUsersFilterKey}
                  hidePriceSort
                />
                <div style={{ paddingTop: user ? 0 : 16 }}>
                  {sortedCreatorIds.length === 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12,
                      padding: '48px 24px',
                      minHeight: 200,
                    }}>
                      <div style={{
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        background: 'rgba(70,245,224,0.08)',
                        border: '1px solid rgba(70,245,224,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Users size={22} color="rgba(70,245,224,0.5)" strokeWidth={1.8} />
                      </div>
                      <p style={{
                        margin: 0,
                        fontSize: 14,
                        color: tokens.textMuted,
                        fontFamily: "'Syne', sans-serif",
                        textAlign: 'center',
                      }}>
                        No creators yet
                      </p>
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(3, 1fr)`,
                      gap: 12,
                      padding: '0 14px 24px',
                    }}>
                      {sortedCreatorIds.map((walletAddress) => {
                        const profile = userProfileById[walletAddress];
                        if (!profile?.username) return null;
                        const avatarUrl = avatarUrlByWallet[walletAddress] ?? null;
                        return (
                          <button
                            key={walletAddress}
                            onClick={() => navigate(`/user/${profile.username}`)}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 8,
                              background: 'rgba(255,255,255,0.03)',
                              border: 'none',
                              borderRadius: 14,
                              padding: '14px 8px 12px',
                              cursor: 'pointer',
                              transition: 'background 0.15s ease',
                              minWidth: 0,
                            }}
                            onMouseEnter={e => {
                              (e.currentTarget as HTMLElement).style.background = 'rgba(70,245,224,0.06)';
                            }}
                            onMouseLeave={e => {
                              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)';
                            }}
                          >
                            {/* Avatar */}
                            <div style={{
                              width: 52,
                              height: 52,
                              borderRadius: '50%',
                              background: 'rgba(70,245,224,0.08)',
                              border: '1.5px solid rgba(70,245,224,0.25)',
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt=""
                                  loading="lazy"
                                  decoding="async"
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <span style={{
                                  fontSize: 18,
                                  fontWeight: 700,
                                  color: 'rgba(70,245,224,0.7)',
                                  fontFamily: "'Syne', sans-serif",
                                  letterSpacing: '-0.02em',
                                  lineHeight: 1,
                                }}>
                                  {profile.username.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            {/* Username */}
                            <p style={{
                              margin: 0,
                              fontSize: 11,
                              fontWeight: 600,
                              color: tokens.textPrimary,
                              fontFamily: "'Syne', sans-serif",
                              letterSpacing: '-0.01em',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                              textAlign: 'center',
                            }}>
                              @{profile.username}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {/* ── End SECTION 3 ────────────────────────────────────────── */}

              </div>
              {/* ── End inner horizontal track ─────────────────────────────── */}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wallpaper Viewer */}
      <AnimatePresence>
        {selectedWallpaper && (
          <WallpaperViewer
            wallpaper={selectedWallpaper}
            onClose={() => {
              // Explore / Favorites / Library scroll position is restored
              // automatically by useScrollRestoration (keyed on
              // selectedWallpaper !== null) — no manual scroll handling here.
              setSelectedWallpaper(null);
              setShuffledViewerList(null);
              viewerFilteredListRef.current = null;
              if (viewerOpenedFromSearchRef.current) {
                // Opened from search — search overlay is still mounted underneath;
                // just clear the flag and let it reappear. Do not clear searchQuery.
                viewerOpenedFromSearchRef.current = false;
                setViewerOpenedFromSearch(false);
              } else if (viewerReturnToSeeAllRef.current) {
                // Opened from "All Wallpapers" — restore that modal instead of Explore
                viewerReturnToSeeAllRef.current = false;
                setShowSeeAllModal(true);
              }
            }}
            isPurchased={purchasedIds.has(selectedWallpaper.id)}
            isFavorited={favoritedIds.has(selectedWallpaper.id)}
            isTrashed={trashedIds.has(selectedWallpaper.id)}
            onPurchase={handlePurchase}
            onToggleFavorite={handleToggleFavorite}
            onTrash={handleTrash}
            purchasing={purchasingId === selectedWallpaper.id}
            skrBalance={skrBalance}
            isAdmin={isAdmin}
            availableCategories={categories.filter(c => c !== 'All')}
            wallpapersList={
              // Use the shuffled list frozen at session-open time.
              // Falls back to the live source only if no shuffle is set yet
              // (e.g. a viewer that opened before this code was deployed).
              shuffledViewerList ?? (
                viewerSource === 'favorites' ? favoriteWallpapers
                : viewerSource === 'library' ? libraryWallpapers
                : viewerSource === 'live' ? (viewerFilteredListRef.current ?? liveWallpaperItems)
                : viewerSource === 'ringtone' ? ringtoneItems
                : (viewerFilteredListRef.current ?? gridWallpapers)
              )
            }
            onNavigate={(wallpaper) => {
              if (resolveMediaType(wallpaper) === 'ringtone') {
                // Activate instant-handoff mode so both viewers skip fade animations.
                setViewerHandoffActive(true);
                setSelectedWallpaper(null);
                // When navigating from a visual item to a ringtone, reuse the
                // already-shuffled viewer list so prev/next stays in the same
                // shuffled order that was established when the session opened.
                const contextPlaylist = shuffledViewerList && shuffledViewerList.length > 0
                  ? shuffledViewerList
                  : viewerSource === 'favorites' ? favoriteWallpapers
                  : viewerSource === 'library' ? libraryWallpapers
                  : ringtoneItems;
                ringtonePlayer.play(wallpaper, contextPlaylist);
                ringtonePlayer.openFullScreen();
                // Clear the handoff flag after one frame so normal animations resume.
                requestAnimationFrame(() => setViewerHandoffActive(false));
              } else {
                lastViewedWallpaperIdRef.current = wallpaper.id;
                setSelectedWallpaper(wallpaper);
              }
            }}
            onAdminDelete={async (wallpaper) => {
              const { deleteWallpapers } = await import('@/lib/collections/wallpapers');
              const ok = await deleteWallpapers(wallpaper.id);
              if (ok) {
                viewerReturnToSeeAllRef.current = false;
                setShuffledViewerList(null);
                setSelectedWallpaper(null);
              } else {
                notify.error('Failed to delete wallpaper', user?.address);
              }
            }}
            onAdminEditCategory={async (wallpaper, newCategory) => {
              const { updateWallpapers } = await import('@/lib/collections/wallpapers');
              const ok = await updateWallpapers(wallpaper.id, { category: newCategory });
              if (!ok) {
                notify.error('Failed to update category', user?.address);
              }
            }}
            instantHandoff={viewerHandoffActive}
          />
        )}
      </AnimatePresence>

      {/* Full-screen ringtone player */}
      <RingtoneFullScreenPlayer
        onToggleFavorite={handleToggleFavorite}
        onPurchase={handlePurchase}
        onTrash={handleTrash}
        purchasing={purchasingId !== null && purchasingId === ringtonePlayer.currentRingtone?.id}
        instantHandoff={viewerHandoffActive}
        onAdvanceToVisual={(item) => {
          // Called when the user swipes from a ringtone to a non-ringtone item
          // (wallpaper / live wallpaper) on Favorites or Library page.
          // Activate instant-handoff mode so both viewers skip fade animations.
          setViewerHandoffActive(true);
          // Close the ringtone player, then open the WallpaperViewer.
          ringtonePlayer.closeFullScreen();
          ringtonePlayer.stop();
          // Ensure viewerSource reflects the current tab context so the viewer's
          // wallpapersList is the correct mixed list for continued swiping.
          // The ringtone player already holds a shuffled playlist — reuse it for
          // the visual viewer so the order is consistent across the handoff.
          const handoffList = ringtonePlayer.playlist.length > 0
            ? ringtonePlayer.playlist
            : activeTab === 'favorites' ? favoriteWallpapers : libraryWallpapers;
          if (activeTab === 'favorites') {
            setViewerSource('favorites');
          } else if (activeTab === 'library') {
            setViewerSource('library');
          }
          lastViewedWallpaperIdRef.current = item.id;
          setShuffledViewerList(handoffList);
          setSelectedWallpaper(item);
          // Clear the handoff flag after one frame so normal animations resume.
          requestAnimationFrame(() => setViewerHandoffActive(false));
        }}
      />

      {/* Admin: Transactions Modal */}
      <AdminTransactionsModal
        isOpen={showTransactions}
        onClose={() => setShowTransactions(false)}
      />

      {/* Admin: Archive Category (bulk zip download of a wallpaper category, backup tooling) */}
      {isAdmin && (
        <AdminArchiveWallpapers
          isOpen={showArchiveCategory}
          onClose={() => setShowArchiveCategory(false)}
          categories={categories.filter(c => c !== 'All')}
          wallpapers={archivableWallpapers}
        />
      )}

      {/* Admin: Add Wallpaper */}
      <AdminAddWallpaper
        isOpen={showAddWallpaper}
        onClose={() => setShowAddWallpaper(false)}
        existingCategories={categories.filter(c => c !== 'All')}
        existingLiveWallpaperCategories={liveWallpaperCategories.filter(c => c !== 'All')}
        existingRingtoneCategories={ringtoneCategories.filter(c => c !== 'All')}
        initialMediaType={(['wallpaper', 'live-wallpaper', 'ringtone'] as const)[activeSection] ?? 'wallpaper'}
        onNewCategory={handleNewCategory}
      />

      {/* Trash View */}
      <TrashView
        isOpen={showTrash}
        onClose={() => setShowTrash(false)}
        userId={user?.address}
        trashItems={userTrash ?? []}
        trashedWallpapers={trashedWallpapers}
        onRestore={handleRestore}
        onDeletePermanently={handleDeletePermanently}
      />

      {/* ── See All Modal (Wallpapers) ─────────────────────────────────────── */}
      <AnimatePresence>
        {showSeeAllModal && (
          <SeeAllModal
            allWallpapers={(allWallpapers ?? []).filter(w => { const s = (w as any).status; return resolveMediaType(w) === 'wallpaper' && (!s || s === 'published') && w.hidden !== true; })}
            categories={categories}
            activeCategory={seeAllCategory}
            onCategoryChange={handleSetSeeAllCategory}
            filterKey={seeAllFilterKey}
            onFilterChange={handleSetSeeAllFilterKey}
            favoritedIds={favoritedIds}
            purchasedIds={purchasedIds}
            onToggleFavorite={handleToggleFavorite}
            onSelectWallpaper={(w, filteredList) => {
              viewerReturnToSeeAllRef.current = true;
              // Store the exact filtered list so swipe-navigation stays within the same category
              viewerFilteredListRef.current = filteredList;
              setShowSeeAllModal(false);
              setViewerSource('grid');
              lastViewedWallpaperIdRef.current = w.id;
              window.history.pushState({ page: 'sub' }, '');
              setShuffledViewerList([...filteredList]);
              setSelectedWallpaper(w);
            }}
            onClose={() => { setShowSeeAllModal(false); setModalNavVisible(true); }}
            onNavVisibility={setModalNavVisible}
            gridColumns={gridColumns}
          />
        )}
      </AnimatePresence>

      {/* ── See All Ringtones Modal ──────────────────────────────────────── */}
      <AnimatePresence>
        {showSeeAllRingtonesModal && (
          <SeeAllRingtonesModal
            allRingtones={(allWallpapers ?? []).filter(w => { const s = (w as any).status; return resolveMediaType(w) === 'ringtone' && (!s || s === 'published') && w.hidden !== true; })}
            categories={['All', ...visibleRingtoneCategories]}
            activeCategory={seeAllRingtonesCategory}
            onCategoryChange={handleSetSeeAllRingtonesCategory}
            favoritedIds={favoritedIds}
            purchasedIds={purchasedIds}
            onToggleFavorite={handleToggleFavorite}
            onClose={() => { setShowSeeAllRingtonesModal(false); setModalNavVisible(true); }}
            onNavVisibility={setModalNavVisible}
          />
        )}
      </AnimatePresence>

      {/* ── See All Live Wallpapers Modal ─────────────────────────────────── */}
      <AnimatePresence>
        {showSeeAllLiveModal && (
          <SeeAllLiveModal
            allLiveWallpapers={(allWallpapers ?? []).filter(w => { const s = (w as any).status; return resolveMediaType(w) === 'live-wallpaper' && (!s || s === 'published') && w.hidden !== true; })}
            categories={liveWallpaperCategories}
            activeCategory={seeAllLiveCategory}
            onCategoryChange={handleSetSeeAllLiveCategory}
            favoritedIds={favoritedIds}
            purchasedIds={purchasedIds}
            onToggleFavorite={handleToggleFavorite}
            onSelectWallpaper={(w, filteredList) => {
              viewerReturnToSeeAllLiveRef.current = true;
              // Store the exact filtered list so swipe-navigation stays within the same category
              viewerFilteredListRef.current = filteredList;
              setShowSeeAllLiveModal(false);
              setViewerSource('live');
              lastViewedWallpaperIdRef.current = w.id;
              window.history.pushState({ page: 'sub' }, '');
              setShuffledViewerList([...filteredList]);
              setSelectedWallpaper(w);
            }}
            onClose={() => { setShowSeeAllLiveModal(false); setModalNavVisible(true); }}
            onNavVisibility={setModalNavVisible}
            gridColumns={gridColumns}
          />
        )}
      </AnimatePresence>

      {/* Manage Categories Sheet (admin only) */}
      {showManageCategories && isAdmin && (
        <ManageCategoriesSheet
          onClose={() => setShowManageCategories(false)}
          categories={categories}
          liveWallpaperCategories={liveWallpaperCategories}
          ringtoneCategories={ringtoneCategories}
          allWallpapers={allWallpapers ?? []}
          resolveMediaType={resolveMediaType}
          onRename={(cat, section) => { setRenameValue(cat); setRenameDialog({ cat, section }); }}
          onDelete={(cat, section) => setDeleteConfirm({ cat, section })}
          onNewCategory={handleNewCategory}
        />
      )}

      {/* Category Context Menu */}
      <AnimatePresence>
        {categoryMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed', inset: 0, zIndex: 3000,
              }}
              onClick={() => setCategoryMenu(null)}
            />
            {/* Floating menu */}
            <motion.div
              initial={{ opacity: 0, scale: 0.88, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.88, y: -6 }}
              transition={{ type: 'spring', stiffness: 420, damping: 26 }}
              style={{
                position: 'fixed',
                left: Math.min(categoryMenu.x, window.innerWidth - 180),
                top: categoryMenu.y + 8,
                zIndex: 3001,
                background: '#1a1a2e',
                border: '1px solid rgba(139,92,246,0.35)',
                borderRadius: 14,
                padding: '6px',
                minWidth: 160,
                boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(139,92,246,0.15)',
                backdropFilter: 'blur(20px)',
              }}
            >
              {/* Category label */}
              <div style={{
                padding: '6px 12px 8px',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
                marginBottom: 4,
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, color: 'rgba(167,139,250,0.6)',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  fontFamily: "'Syne', sans-serif",
                }}>
                  {categoryMenu.cat}
                </span>
              </div>

              {/* Rename option */}
              <motion.button
                whileHover={{ background: 'rgba(139,92,246,0.15)' }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setRenameValue(categoryMenu.cat);
                  setRenameDialog({ cat: categoryMenu.cat, section: categoryMenu.section ?? 'wallpaper' });
                  setCategoryMenu(null);
                }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 9, border: 'none',
                  background: 'transparent', cursor: 'pointer',
                  color: '#f0f0f5', fontSize: 13, fontWeight: 600,
                  fontFamily: "'Syne', sans-serif",
                  textAlign: 'left',
                  transition: 'background 0.15s',
                }}
              >
                <Pencil size={14} color="#a78bfa" />
                Rename
              </motion.button>

              {/* Clear option */}
              <motion.button
                whileHover={{ background: 'rgba(245,158,11,0.12)' }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setClearConfirm({ cat: categoryMenu.cat, section: categoryMenu.section ?? 'wallpaper' });
                  setCategoryMenu(null);
                }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 9, border: 'none',
                  background: 'transparent', cursor: 'pointer',
                  color: 'rgba(251,191,36,0.9)', fontSize: 13, fontWeight: 600,
                  fontFamily: "'Syne', sans-serif",
                  textAlign: 'left',
                  transition: 'background 0.15s',
                }}
              >
                <Eraser size={14} color="rgba(251,191,36,0.85)" />
                Clear
              </motion.button>

              {/* Delete option */}
              <motion.button
                whileHover={{ background: 'rgba(239,68,68,0.12)' }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setDeleteConfirm({ cat: categoryMenu.cat, section: categoryMenu.section ?? 'wallpaper' });
                  setCategoryMenu(null);
                }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 9, border: 'none',
                  background: 'transparent', cursor: 'pointer',
                  color: 'rgba(248,113,113,0.9)', fontSize: 13, fontWeight: 600,
                  fontFamily: "'Syne', sans-serif",
                  textAlign: 'left',
                  transition: 'background 0.15s',
                }}
              >
                <Trash size={14} color="rgba(248,113,113,0.85)" />
                Delete
              </motion.button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Rename Category Dialog */}
      <AnimatePresence>
        {renameDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 4500,
              background: 'rgba(0,0,0,0.7)',
              backdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 20px',
            }}
            onClick={() => { if (!categoryActionLoading) setRenameDialog(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 380, damping: 26 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: '#141420',
                borderRadius: 20,
                padding: '24px 22px',
                border: '1px solid rgba(139,92,246,0.3)',
                maxWidth: 360, width: '100%',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(139,92,246,0.1)',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'rgba(139,92,246,0.15)',
                  border: '1px solid rgba(139,92,246,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Pencil size={15} color="#a78bfa" />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#f0f0f5', fontFamily: "'Syne', sans-serif" }}>
                    Rename Category
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(240,240,245,0.4)', marginTop: 1 }}>
                    Updates all items in "{renameDialog.cat}"
                  </div>
                </div>
              </div>

              {/* Input */}
              <input
                autoFocus
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCategoryRename(); if (e.key === 'Escape') setRenameDialog(null); }}
                placeholder="New category name..."
                disabled={categoryActionLoading}
                style={{
                  width: '100%', padding: '11px 14px',
                  borderRadius: 12, border: '1px solid rgba(139,92,246,0.25)',
                  background: 'rgba(255,255,255,0.05)', color: '#f0f0f5',
                  fontSize: 14, outline: 'none', boxSizing: 'border-box',
                  fontFamily: "'Syne', sans-serif",
                  marginBottom: 16,
                }}
              />

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10 }}>
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  onClick={() => setRenameDialog(null)}
                  disabled={categoryActionLoading}
                  style={{
                    flex: 1, padding: '11px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(240,240,245,0.65)', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                    opacity: categoryActionLoading ? 0.5 : 1,
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  onClick={handleCategoryRename}
                  disabled={categoryActionLoading || !renameValue.trim()}
                  style={{
                    flex: 1, padding: '11px', borderRadius: 12,
                    background: renameValue.trim() ? 'rgba(139,92,246,0.85)' : 'rgba(139,92,246,0.3)',
                    border: '1px solid rgba(167,139,250,0.4)',
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    cursor: renameValue.trim() ? 'pointer' : 'not-allowed',
                    fontFamily: "'Syne', sans-serif",
                    opacity: categoryActionLoading ? 0.7 : 1,
                  }}
                >
                  {categoryActionLoading ? 'Saving...' : 'Rename'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clear Category Confirmation */}
      <AnimatePresence>
        {clearConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 4500,
              background: 'rgba(0,0,0,0.72)',
              backdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 20px',
            }}
            onClick={() => { if (!categoryActionLoading) setClearConfirm(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 380, damping: 26 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: '#141420',
                borderRadius: 20,
                padding: '24px 22px',
                border: '1px solid rgba(245,158,11,0.25)',
                maxWidth: 360, width: '100%',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(245,158,11,0.08)',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'rgba(245,158,11,0.12)',
                  border: '1px solid rgba(245,158,11,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Eraser size={15} color="rgba(251,191,36,0.9)" />
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#f0f0f5', fontFamily: "'Syne', sans-serif" }}>
                  Clear Category
                </div>
              </div>

              <p style={{
                fontSize: 13, color: 'rgba(240,240,245,0.55)', lineHeight: 1.65,
                margin: '0 0 18px',
              }}>
                Remove all items from category <strong style={{ color: '#f0f0f5' }}>"{clearConfirm.cat}"</strong>?
                The category will remain in the list but will be empty. Items will move to uncategorized.
              </p>

              <div style={{ display: 'flex', gap: 10 }}>
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  onClick={() => setClearConfirm(null)}
                  disabled={categoryActionLoading}
                  style={{
                    flex: 1, padding: '11px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(240,240,245,0.65)', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                    opacity: categoryActionLoading ? 0.5 : 1,
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  onClick={handleCategoryClear}
                  disabled={categoryActionLoading}
                  style={{
                    flex: 1, padding: '11px', borderRadius: 12,
                    background: 'rgba(245,158,11,0.8)',
                    border: '1px solid rgba(251,191,36,0.4)',
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                    opacity: categoryActionLoading ? 0.7 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  <Eraser size={14} />
                  {categoryActionLoading ? 'Clearing...' : 'Clear'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Category Confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 4500,
              background: 'rgba(0,0,0,0.72)',
              backdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 20px',
            }}
            onClick={() => { if (!categoryActionLoading) setDeleteConfirm(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 380, damping: 26 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: '#141420',
                borderRadius: 20,
                padding: '24px 22px',
                border: '1px solid rgba(239,68,68,0.25)',
                maxWidth: 360, width: '100%',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(239,68,68,0.08)',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'rgba(239,68,68,0.12)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Trash size={15} color="rgba(248,113,113,0.9)" />
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#f0f0f5', fontFamily: "'Syne', sans-serif" }}>
                  Delete Category
                </div>
              </div>

              <p style={{
                fontSize: 13, color: 'rgba(240,240,245,0.55)', lineHeight: 1.65,
                margin: '0 0 18px',
              }}>
                Permanently delete category <strong style={{ color: '#f0f0f5' }}>"{deleteConfirm.cat}"</strong>?
                All items will become uncategorized and the category will be removed from the list.
              </p>

              <div style={{ display: 'flex', gap: 10 }}>
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  onClick={() => setDeleteConfirm(null)}
                  disabled={categoryActionLoading}
                  style={{
                    flex: 1, padding: '11px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(240,240,245,0.65)', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                    opacity: categoryActionLoading ? 0.5 : 1,
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  onClick={handleCategoryDelete}
                  disabled={categoryActionLoading}
                  style={{
                    flex: 1, padding: '11px', borderRadius: 12,
                    background: 'rgba(239,68,68,0.8)',
                    border: '1px solid rgba(248,113,113,0.4)',
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                    opacity: categoryActionLoading ? 0.7 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  <Trash size={14} />
                  {categoryActionLoading ? 'Deleting...' : 'Delete'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .wallpaper-grid { grid-template-columns: repeat(2, 1fr); }
        @media (min-width: 768px) { .wallpaper-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (min-width: 1024px) { .wallpaper-grid { grid-template-columns: repeat(4, 1fr); } }
        ::-webkit-scrollbar { display: none; }
        * { -ms-overflow-style: none; scrollbar-width: none; }
        input::placeholder { color: rgba(240,240,245,0.28); }
        @keyframes ringtonePulseHP {
          0% { opacity: 0.8; transform: scale(1); }
          100% { opacity: 1; transform: scale(1.08); }
        }
        @keyframes ringtoneGlowHP {
          0% { opacity: 0.6; }
          100% { opacity: 1; }
        }
        @keyframes waveBarHP1 {
          0% { transform: scaleY(0.5); } 100% { transform: scaleY(1); }
        }
        @keyframes waveBarHP2 {
          0% { transform: scaleY(0.7); } 100% { transform: scaleY(0.4); }
        }
        @keyframes waveBarHP3 {
          0% { transform: scaleY(1); } 100% { transform: scaleY(0.6); }
        }
        @keyframes waveBarHP4 {
          0% { transform: scaleY(0.4); } 100% { transform: scaleY(0.9); }
        }
      `}</style>

      {/* My Uploads Panel */}
      <MyUploadsPanel
        isOpen={showMyUploads}
        onClose={() => setShowMyUploads(false)}
        userAddress={user?.address ?? ''}
        showUploadModal={showUserUploadModal}
        onOpenUploadModal={() => setShowUserUploadModal(true)}
        onCloseUploadModal={() => setShowUserUploadModal(false)}
      />

      {/* Profile Setup Gate — shown when user tries to upload without a complete profile */}
      {user && (
        <ProfileSetupGate
          isOpen={showProfileSetupGate}
          walletAddress={user.address}
          onProfileComplete={() => {
            setShowProfileSetupGate(false);
            setShowMyUploads(true);
          }}
          onClose={() => setShowProfileSetupGate(false)}
        />
      )}

      {/* Admin Moderation Panel */}
      {isAdmin && (
        <AdminModerationPanel
          isOpen={showModeration}
          onClose={() => setShowModeration(false)}
          onFullViewChange={setModerationFullViewOpen}
        />
      )}

      {/* Subscriptions Dialog */}
      {user && (
        <SubscriptionsDialog
          open={showSubscriptionsDialog}
          onClose={() => setShowSubscriptionsDialog(false)}
          userAddress={user.address}
        />
      )}
    </CategoryPageShell>
  );
}

// ── See All Modal ──────────────────────────────────────────────────────────────

// Session-stable shuffle: generates an order on fresh mount, reuses it when
// returning from the wallpaper viewer so scroll restoration works correctly.
function useStableShuffleOrder<T extends { id: string }>(
  items: T[],
  sessionKey: string,
  returningKey: string,
): T[] {
  // shuffleOrder: array of IDs in display order, stable for this visit
  const [shuffleOrder, setShuffleOrder] = useState<string[]>([]);
  // Track whether we've been initialized with a non-empty set
  const initializedWithDataRef = useRef(false);
  // Track whether we're in returning-from-viewer mode (decided at first render)
  const returningModeRef = useRef<boolean | null>(null);

  // Decide mode synchronously on very first render (before any effects run)
  if (returningModeRef.current === null) {
    try {
      returningModeRef.current = sessionStorage.getItem(returningKey) === 'true';
      if (returningModeRef.current) {
        sessionStorage.removeItem(returningKey);
      }
    } catch {
      returningModeRef.current = false;
    }
  }

  // (Re-)initialize the shuffle order whenever we receive data for the first time
  useEffect(() => {
    if (initializedWithDataRef.current || items.length === 0) return;
    initializedWithDataRef.current = true;

    if (returningModeRef.current) {
      // Restore existing shuffle order from sessionStorage
      try {
        const stored = sessionStorage.getItem(sessionKey);
        if (stored) {
          const order: string[] = JSON.parse(stored);
          setShuffleOrder(order);
          return;
        }
      } catch {}
    }

    // Fresh mount — generate and store a new shuffle
    const ids = items.map(item => item.id);
    // Fisher-Yates shuffle
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify(ids));
    } catch {}
    setShuffleOrder(ids);
  // Trigger when items arrive (length transitions from 0 to non-zero)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, sessionKey]);

  return useMemo(() => {
    if (shuffleOrder.length === 0) return items;
    const posMap = new Map<string, number>(shuffleOrder.map((id, idx) => [id, idx]));
    return [...items].sort((a, b) => {
      const aPos = posMap.get(a.id) ?? items.length;
      const bPos = posMap.get(b.id) ?? items.length;
      return aPos - bPos;
    });
  }, [items, shuffleOrder]);
}

interface SeeAllModalProps {
  allWallpapers: WallpapersResponse[];
  categories: string[];
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
  filterKey: SeeAllFilterKey;
  onFilterChange: (key: SeeAllFilterKey) => void;
  favoritedIds: Set<string>;
  purchasedIds: Set<string>;
  onToggleFavorite: (w: WallpapersResponse) => void;
  onSelectWallpaper: (w: WallpapersResponse, filteredList: WallpapersResponse[]) => void;
  onClose: () => void;
  onNavVisibility?: (visible: boolean) => void;
  gridColumns?: number;
}

const SeeAllModal: React.FC<SeeAllModalProps> = ({
  allWallpapers,
  categories,
  activeCategory,
  onCategoryChange,
  filterKey,
  onFilterChange,
  favoritedIds,
  purchasedIds,
  onToggleFavorite,
  onSelectWallpaper,
  onClose,
  onNavVisibility,
  gridColumns = 2,
}) => {
  const { tokens } = useAppTheme();
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollContainerEl, setScrollContainerEl] = useState<HTMLDivElement | null>(null);

  // Filter dropdown open/close state (UI only — filterKey is now lifted to parent)
  const [filterOpen, setFilterOpen] = useState(false);

  // On mount (or after remount from viewer), scroll the active category chip into
  // view so the strip lands where the user left it. Uses 'instant' behavior to
  // avoid a visible scroll animation on open.
  useLayoutEffect(() => {
    const container = categoryScrollRef.current;
    if (!container) return;
    // Find the active pill button inside the strip container
    const activePill = container.querySelector<HTMLElement>('[data-active-category="true"]');
    if (activePill) {
      activePill.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' } as ScrollIntoViewOptions);
    }
  }, []); // run once on mount

  // Hide the page title when the user scrolls down; bring it back near top.
  // Uses hysteresis (40px down / 10px up) so it doesn't flicker at the threshold.
  const [titleHidden, setTitleHidden] = useState(false);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const handleScroll = debounce(() => {
      const top = el.scrollTop;
      setTitleHidden(prev => {
        if (!prev && top > 40) return true;
        if (prev && top < 10) return false;
        return prev;
      });
    });
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide the bottom nav while scrolling; restore 1.3s after scroll stops.
  const navVisibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!onNavVisibility) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const handleScroll = debounce(() => {
      onNavVisibility(false);
      if (navVisibilityTimerRef.current) clearTimeout(navVisibilityTimerRef.current);
      navVisibilityTimerRef.current = setTimeout(() => {
        onNavVisibility(true);
      }, 1300);
    });
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (navVisibilityTimerRef.current) clearTimeout(navVisibilityTimerRef.current);
      onNavVisibility(true);
    };
  }, [onNavVisibility]);

  // Apply stable shuffle to the full list first, then filter by category.
  // This keeps the shuffle consistent across category switches and returns
  // from the viewer.
  const shuffledWallpapers = useStableShuffleOrder(
    allWallpapers,
    'wallpapers-shuffle-order',
    'wallpapers-returning-from-viewer',
  );

  // Compute which category pills to show — only categories that have at least one wallpaper
  const populatedCategories = useMemo(() => {
    const populated = new Set<string>();
    allWallpapers.forEach(w => { if (w.category) populated.add(w.category.toLowerCase()); });
    return categories.filter(cat => cat === 'All' || populated.has(cat.toLowerCase()));
  }, [allWallpapers, categories]);

  // Freshness-based ordering for the populated categories
  const { sortedCategories: sortedWallpaperCategories } = useCategoryOrder(populatedCategories, allWallpapers);

  // Filter wallpapers by selected category (applied AFTER shuffle)
  const filteredItems = useMemo(() => {
    if (activeCategory === 'All') return shuffledWallpapers;
    return shuffledWallpapers.filter(w => w.category?.toLowerCase() === activeCategory.toLowerCase());
  }, [shuffledWallpapers, activeCategory]);

  // Apply sort/filter based on filterKey
  const sortedItems = useMemo(
    () => sortByFilterKey(filteredItems, filterKey),
    [filteredItems, filterKey]
  );

  // Restore scroll position when returning from viewer.
  // Re-runs when sortedItems arrives so scrollIntoView can find the DOM element.
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (scrollRestoredRef.current) return;
    const savedScroll = sessionStorage.getItem('wallpapers-scroll-position');
    const savedId = sessionStorage.getItem('wallpapers-last-viewed-id');
    if (savedScroll === null && savedId === null) return;
    if (sortedItems.length === 0) return; // wait for data

    scrollRestoredRef.current = true;
    // Give the grid a tick to render before scrolling
    const timer = setTimeout(() => {
      if (savedId) {
        const el = document.getElementById(`wallpaper-${savedId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'instant', block: 'center' });
          sessionStorage.removeItem('wallpapers-last-viewed-id');
          sessionStorage.removeItem('wallpapers-scroll-position');
          return;
        }
      }
      if (savedScroll !== null && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = Number(savedScroll);
      }
      sessionStorage.removeItem('wallpapers-scroll-position');
      sessionStorage.removeItem('wallpapers-last-viewed-id');
    }, 80);
    return () => clearTimeout(timer);
  }, [sortedItems.length]);

  // Escape key to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Lock body scroll while modal is open
  useEffect(() => {
    const unlock = lockBodyScroll();
    return unlock;
  }, []);

  return (
    <motion.div
      key="see-all-modal"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 24 }}
      transition={{ type: 'spring', stiffness: 340, damping: 32 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        background: tokens.bg,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 210,
        background: tokens.bgHeader,
        flexShrink: 0,
        paddingTop: `env(safe-area-inset-top, 0px)`,
      }}>
        {/* Title row — matches Library (CategoryPageShell). Collapses on scroll. */}
        <motion.div
          initial={false}
          animate={{
            height: titleHidden ? 0 : 'auto',
            opacity: titleHidden ? 0 : 1,
            y: titleHidden ? -16 : 0,
          }}
          transition={{ type: 'spring', stiffness: 320, damping: 32, mass: 0.7 }}
          style={{ overflow: 'hidden' }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px 12px',
          }}>
            <button
              onClick={onClose}
              aria-label="Back"
              style={{
                width: 36, height: 36,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(240,240,245,0.7)',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <ChevronLeft size={24} strokeWidth={2} />
            </button>
            <span style={{
              fontSize: 18,
              fontWeight: 800,
              fontFamily: "'Syne', sans-serif",
              letterSpacing: '-0.02em',
              textAlign: 'left',
              flex: 1,
              marginLeft: 8,
              backgroundImage: 'linear-gradient(135deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
            }}>
              All Wallpapers
            </span>
          </div>
        </motion.div>

        {/* Category filter scroll row */}
        <div
          ref={categoryScrollRef}
          style={{
            display: 'flex',
            gap: 6,
            overflowX: 'auto',
            padding: '16px 14px 16px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          } as React.CSSProperties}
        >
          {/* Filter dropdown pill — replaces the "All" category pill */}
          <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
            <DropdownMenuTrigger asChild>
              <motion.button
                whileTap={{ scale: 0.94 }}
                data-active-category={(filterKey !== 'all' || activeCategory === 'All') ? 'true' : undefined}
                style={{
                  flexShrink: 0,
                  padding: '5px 14px',
                  borderRadius: 999,
                  border: (filterKey !== 'all' || activeCategory === 'All')
                    ? '1px solid transparent'
                    : '1px solid rgba(255,255,255,0.13)',
                  background: (filterKey !== 'all' || activeCategory === 'All') ? '#46f5e0' : 'transparent',
                  color: (filterKey !== 'all' || activeCategory === 'All') ? '#0a0a10' : 'rgba(240,240,245,0.5)',
                  fontSize: 12,
                  fontWeight: (filterKey !== 'all' || activeCategory === 'All') ? 700 : 500,
                  cursor: 'pointer',
                  fontFamily: "'Syne', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  whiteSpace: 'nowrap',
                  letterSpacing: (filterKey !== 'all' || activeCategory === 'All') ? '-0.01em' : '0',
                  boxShadow: 'none',
                  transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                }}
              >
                <Filter size={11} strokeWidth={2.2} />
                {SEE_ALL_FILTER_LABELS[filterKey]}
              </motion.button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={8}
              style={{
                background: 'rgba(20,18,28,0.98)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 14,
                minWidth: 220,
                padding: 6,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
                zIndex: 10000,
              }}
            >
              <div style={{
                padding: '8px 12px 6px',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'rgba(240,240,245,0.42)',
                fontFamily: "'Syne', sans-serif",
              }}>
                Sort by
              </div>
              {(Object.entries(SEE_ALL_FILTER_LABELS) as [SeeAllFilterKey, string][]).map(([key, label]) => {
                // For the "All" item, only mark selected when filterKey is 'all' AND no specific
                // category chip is active (activeCategory === 'All'). For other keys (e.g. 'new'),
                // the checkmark follows filterKey alone since those items always reset activeCategory.
                const selected = key === 'all'
                  ? filterKey === 'all' && activeCategory === 'All'
                  : filterKey === key;
                return (
                  <DropdownMenuItem
                    key={key}
                    onSelect={(e) => { e.preventDefault(); onFilterChange(key); onCategoryChange('All'); setFilterOpen(false); }}
                    style={{
                      fontFamily: "'Syne', sans-serif",
                      fontSize: 13,
                      fontWeight: selected ? 700 : 500,
                      color: selected ? '#46f5e0' : 'rgba(240,240,245,0.78)',
                      cursor: 'pointer',
                      borderRadius: 8,
                      padding: '9px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span>{label}</span>
                    {selected && <Check size={14} strokeWidth={2.5} color="#46f5e0" />}
                  </DropdownMenuItem>
                );
              })}

              <div style={{
                height: 1,
                background: 'rgba(255,255,255,0.08)',
                margin: '6px 4px',
              }} />

              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); onFilterChange('all'); setFilterOpen(false); }}
                style={{
                  fontFamily: "'Syne', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: filterKey !== 'all' ? '#f0f0f5' : 'rgba(240,240,245,0.4)',
                  cursor: filterKey !== 'all' ? 'pointer' : 'default',
                  borderRadius: 8,
                  padding: '9px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <RotateCcw size={13} strokeWidth={2.2} />
                Reset filter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Category pills — skip "All" since the filter pill replaces it; ordered by freshness */}
          {sortedWallpaperCategories.filter(cat => cat !== 'All').map(cat => {
            const isActive = cat === activeCategory;
            return (
              <motion.button
                key={cat}
                whileTap={{ scale: 0.94 }}
                data-active-category={isActive ? 'true' : undefined}
                onClick={() => { onCategoryChange(cat); onFilterChange('all'); }}
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  flexShrink: 0,
                  padding: '5px 14px',
                  borderRadius: 999,
                  border: isActive
                    ? '1px solid transparent'
                    : '1px solid rgba(255,255,255,0.13)',
                  background: isActive
                    ? '#46f5e0'
                    : 'transparent',
                  color: isActive ? '#0a0a10' : 'rgba(240,240,245,0.5)',
                  fontSize: 12, fontWeight: isActive ? 700 : 500,
                  fontFamily: "'Syne', sans-serif",
                  cursor: 'pointer',
                  transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                  letterSpacing: isActive ? '-0.01em' : '0',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {cat}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Grid — virtualized for performance */}
      <div
        ref={(el) => { (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el; setScrollContainerEl(el); }}
        style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        {sortedItems.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '80px 24px', gap: 14,
            textAlign: 'center',
          }}>
            <ImageIcon size={36} color="rgba(139,92,246,0.5)" />
            <p style={{ color: 'rgba(240,240,245,0.4)', fontSize: 14, margin: 0 }}>
              No wallpapers in {activeCategory}
            </p>
          </div>
        ) : (
          <VirtualWallpaperGrid
            wallpapers={sortedItems}
            favoritedIds={favoritedIds}
            purchasedIds={purchasedIds}
            onToggleFavorite={onToggleFavorite}
            onSelectWallpaper={(w) => {
              try {
                sessionStorage.setItem('wallpapers-scroll-position', String(scrollContainerRef.current?.scrollTop ?? 0));
                sessionStorage.setItem('wallpapers-last-viewed-id', w.id);
                sessionStorage.setItem('wallpapers-returning-from-viewer', 'true');
              } catch {}
              onSelectWallpaper(w, sortedItems);
            }}
            gridColumns={gridColumns}
            minimal
            scrollContainer={scrollContainerEl}
          />
        )}
      </div>
    </motion.div>
  );
};

// ── See All Ringtones Modal ───────────────────────────────────────────────────

interface SeeAllRingtonesModalProps {
  allRingtones: WallpapersResponse[];
  categories: string[];
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
  favoritedIds: Set<string>;
  purchasedIds: Set<string>;
  onToggleFavorite: (w: WallpapersResponse) => void;
  onClose: () => void;
  onNavVisibility?: (visible: boolean) => void;
}

const SeeAllRingtonesModal: React.FC<SeeAllRingtonesModalProps> = ({
  allRingtones,
  categories,
  activeCategory,
  onCategoryChange,
  favoritedIds,
  purchasedIds,
  onToggleFavorite,
  onClose,
  onNavVisibility,
}) => {
  const { tokens } = useAppTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const ringtoneCategoryScrollRef = useRef<HTMLDivElement>(null);

  // On mount, scroll the active category chip into view so the strip restores
  // its horizontal position after reopening.
  useLayoutEffect(() => {
    const container = ringtoneCategoryScrollRef.current;
    if (!container) return;
    const activePill = container.querySelector<HTMLElement>('[data-active-category="true"]');
    if (activePill) {
      activePill.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' } as ScrollIntoViewOptions);
    }
  }, []); // run once on mount

  // Freshness-based sorting for category chips
  const { sortedCategories: sortedRingtoneCategories, incrementCategory: incrementRingtoneCategory } = useCategoryOrder(categories, allRingtones);

  // Apply stable shuffle to full ringtone list; filter by category after
  const shuffledRingtones = useStableShuffleOrder(
    allRingtones,
    'ringtones-shuffle-order',
    'ringtones-returning-from-viewer',
  );

  const filteredItems = useMemo(() => {
    if (activeCategory === 'All') return shuffledRingtones;
    return shuffledRingtones.filter(w => w.category?.toLowerCase() === activeCategory.toLowerCase());
  }, [shuffledRingtones, activeCategory]);

  // The shuffled + filtered list IS the display order — no additional sort
  const sortedItems = filteredItems;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    const unlock = lockBodyScroll();
    return unlock;
  }, []);

  // Hide the bottom nav while scrolling; restore 1.3s after scroll stops.
  const ringtoneNavTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!onNavVisibility) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const handleScroll = debounce(() => {
      onNavVisibility(false);
      if (ringtoneNavTimerRef.current) clearTimeout(ringtoneNavTimerRef.current);
      ringtoneNavTimerRef.current = setTimeout(() => {
        onNavVisibility(true);
      }, 1300);
    });
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (ringtoneNavTimerRef.current) clearTimeout(ringtoneNavTimerRef.current);
      onNavVisibility(true);
    };
  }, [onNavVisibility]);

  return (
    <motion.div
      key="see-all-ringtones-modal"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 24 }}
      transition={{ type: 'spring', stiffness: 340, damping: 32 }}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9999,
        background: tokens.bg,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 210,
        background: tokens.bgHeader,
        flexShrink: 0,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px 12px',
          paddingTop: `calc(14px + env(safe-area-inset-top, 0px))`,
        }}>
          <button
            onClick={onClose}
            aria-label="Back"
            style={{
              width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(240,240,245,0.7)',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <ChevronLeft size={24} strokeWidth={2} />
          </button>
          <span style={{
            fontSize: 18, fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
            letterSpacing: '-0.02em', textAlign: 'left', flex: 1,
            marginLeft: 8,
            backgroundImage: 'linear-gradient(135deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}>
            All Ringtones
          </span>
        </div>

        {/* Category pills (mint) */}
        <div
          ref={ringtoneCategoryScrollRef}
          style={{
            display: 'flex', gap: 6,
            overflowX: 'auto',
            padding: '16px 14px 16px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          } as React.CSSProperties}
        >
          {sortedRingtoneCategories.map(cat => {
            const isActive = cat === activeCategory;
            return (
              <button
                key={cat}
                data-active-category={isActive ? 'true' : undefined}
                onClick={() => { incrementRingtoneCategory(cat); onCategoryChange(cat); }}
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  flexShrink: 0,
                  padding: '5px 14px',
                  borderRadius: 999,
                  border: isActive ? 'none' : '1px solid rgba(255,255,255,0.13)',
                  background: isActive ? '#46f5e0' : 'transparent',
                  color: isActive ? '#0a0a10' : 'rgba(240,240,245,0.5)',
                  fontSize: 12, fontWeight: isActive ? 700 : 500,
                  fontFamily: "'Syne', sans-serif",
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  letterSpacing: isActive ? '-0.01em' : '0',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div
        ref={scrollContainerRef}
        style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        {sortedItems.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '80px 24px', gap: 14, textAlign: 'center',
          }}>
            <Bell size={36} color="rgba(70,245,224,0.5)" />
            <p style={{ color: 'rgba(240,240,245,0.4)', fontSize: 14, margin: 0 }}>
              No ringtones in {activeCategory}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 7px 110px' }}>
            {sortedItems.map(item => (
              <AdminEditableCard key={item.id} item={item}>
                <RingtoneRow
                  wallpaper={item}
                  isFavorited={favoritedIds.has(item.id)}
                  isPurchased={purchasedIds.has(item.id)}
                  playlist={sortedItems}
                  onToggleFavorite={onToggleFavorite}
                />
              </AdminEditableCard>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ── See All Live Wallpapers Modal ─────────────────────────────────────────────

interface SeeAllLiveModalProps {
  allLiveWallpapers: WallpapersResponse[];
  categories: string[];
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
  favoritedIds: Set<string>;
  purchasedIds: Set<string>;
  onToggleFavorite: (w: WallpapersResponse) => void;
  onSelectWallpaper: (w: WallpapersResponse, filteredList: WallpapersResponse[]) => void;
  onClose: () => void;
  onNavVisibility?: (visible: boolean) => void;
  gridColumns?: number;
}

const SeeAllLiveModal: React.FC<SeeAllLiveModalProps> = ({
  allLiveWallpapers,
  categories,
  activeCategory,
  onCategoryChange,
  favoritedIds,
  purchasedIds,
  onToggleFavorite,
  onSelectWallpaper,
  onClose,
  onNavVisibility,
  gridColumns = 2,
}) => {
  const { tokens } = useAppTheme();
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollContainerEl, setScrollContainerEl] = useState<HTMLDivElement | null>(null);

  // Scroll the active category chip into view on mount
  useLayoutEffect(() => {
    const container = categoryScrollRef.current;
    if (!container) return;
    const activePill = container.querySelector<HTMLElement>('[data-active-category="true"]');
    if (activePill) {
      activePill.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' } as ScrollIntoViewOptions);
    }
  }, []);

  // Freshness-based sorting for category chips
  const { sortedCategories: sortedLiveCategories, incrementCategory: incrementLiveCategory } = useCategoryOrder(categories, allLiveWallpapers);

  // Session-stable shuffle for the live wallpaper list
  const shuffledLive = useStableShuffleOrder(
    allLiveWallpapers,
    'live-wallpapers-shuffle-order',
    'live-wallpapers-returning-from-viewer',
  );

  const filteredItems = useMemo(() => {
    if (activeCategory === 'All') return shuffledLive;
    return shuffledLive.filter(w => w.category?.toLowerCase() === activeCategory.toLowerCase());
  }, [shuffledLive, activeCategory]);

  // Restore scroll position when returning from viewer
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (scrollRestoredRef.current) return;
    const savedId = sessionStorage.getItem('live-wallpapers-last-viewed-id');
    const savedScroll = sessionStorage.getItem('live-wallpapers-scroll-position');
    if (savedScroll === null && savedId === null) return;
    if (filteredItems.length === 0) return;

    scrollRestoredRef.current = true;
    const timer = setTimeout(() => {
      if (savedId) {
        const el = document.getElementById(`wallpaper-${savedId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'instant', block: 'center' });
          sessionStorage.removeItem('live-wallpapers-last-viewed-id');
          sessionStorage.removeItem('live-wallpapers-scroll-position');
          return;
        }
      }
      if (savedScroll !== null && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = Number(savedScroll);
      }
      sessionStorage.removeItem('live-wallpapers-scroll-position');
      sessionStorage.removeItem('live-wallpapers-last-viewed-id');
    }, 80);
    return () => clearTimeout(timer);
  }, [filteredItems.length]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    const unlock = lockBodyScroll();
    return unlock;
  }, []);

  // Hide bottom nav while scrolling; restore after 1.3s
  const liveNavTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!onNavVisibility) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const handleScroll = debounce(() => {
      onNavVisibility(false);
      if (liveNavTimerRef.current) clearTimeout(liveNavTimerRef.current);
      liveNavTimerRef.current = setTimeout(() => { onNavVisibility(true); }, 1300);
    });
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (liveNavTimerRef.current) clearTimeout(liveNavTimerRef.current);
      onNavVisibility(true);
    };
  }, [onNavVisibility]);

  return (
    <motion.div
      key="see-all-live-modal"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 24 }}
      transition={{ type: 'spring', stiffness: 340, damping: 32 }}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9999,
        background: tokens.bg,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 210,
        background: tokens.bgHeader,
        flexShrink: 0,
        paddingTop: `env(safe-area-inset-top, 0px)`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px 12px',
        }}>
          <button
            onClick={onClose}
            aria-label="Back"
            style={{
              width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(240,240,245,0.7)',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <ChevronLeft size={24} strokeWidth={2} />
          </button>
          <span style={{
            fontSize: 18, fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
            letterSpacing: '-0.02em', textAlign: 'left', flex: 1,
            marginLeft: 8,
            backgroundImage: 'linear-gradient(135deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}>
            All Live Wallpapers
          </span>
        </div>

        {/* Category pills */}
        <div
          ref={categoryScrollRef}
          style={{
            display: 'flex', gap: 6,
            overflowX: 'auto',
            padding: '16px 14px 16px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          } as React.CSSProperties}
        >
          {sortedLiveCategories.map(cat => {
            const isActive = cat === activeCategory;
            return (
              <button
                key={cat}
                data-active-category={isActive ? 'true' : undefined}
                onClick={() => { incrementLiveCategory(cat); onCategoryChange(cat); }}
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  flexShrink: 0,
                  padding: '5px 14px',
                  borderRadius: 999,
                  border: isActive ? 'none' : '1px solid rgba(255,255,255,0.13)',
                  background: isActive ? '#46f5e0' : 'transparent',
                  color: isActive ? '#0a0a10' : 'rgba(240,240,245,0.5)',
                  fontSize: 12, fontWeight: isActive ? 700 : 500,
                  fontFamily: "'Syne', sans-serif",
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  letterSpacing: isActive ? '-0.01em' : '0',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      <div
        ref={(el) => { (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el; setScrollContainerEl(el); }}
        style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        {filteredItems.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '80px 24px', gap: 14, textAlign: 'center',
          }}>
            <Film size={36} color="rgba(167,139,250,0.5)" />
            <p style={{ color: 'rgba(240,240,245,0.4)', fontSize: 14, margin: 0 }}>
              No live wallpapers{activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
            </p>
          </div>
        ) : (
          <VirtualWallpaperGrid
            wallpapers={filteredItems}
            favoritedIds={favoritedIds}
            purchasedIds={purchasedIds}
            onToggleFavorite={onToggleFavorite}
            onSelectWallpaper={(w) => {
              try {
                sessionStorage.setItem('live-wallpapers-scroll-position', String(scrollContainerRef.current?.scrollTop ?? 0));
                sessionStorage.setItem('live-wallpapers-last-viewed-id', w.id);
                sessionStorage.setItem('live-wallpapers-returning-from-viewer', 'true');
              } catch {}
              onSelectWallpaper(w, filteredItems);
            }}
            gridColumns={gridColumns}
            minimal
            scrollContainer={scrollContainerEl}
          />
        )}
      </div>
    </motion.div>
  );
};

export default HomePage;
